import { useState, useEffect, useMemo } from 'react';
import { useConfig } from '../context/ConfigContext';
import PendingChangesBanner from './PendingChangesBanner';
// sortTimeSlots is the app-wide slot ordering (see parseTimeSlotStartHour in
// bookingUtils.js). This screen used to carry two private re-implementations
// of it that disagreed with it and with each other — see handleAddTimeSlot.
import { buildDefaultFaqs, formatHourLabel, getSportRateBands, WEEKDAYS, describeBandDays, getBookingWindowDays, getAllConfiguredTimeSlots, DEFAULT_ADDON_REQUIRED_MESSAGE } from '../utils/bookingUtils';
import { DEFAULT_NEXT_DAY_GRID_END_HOUR, MAX_NEXT_DAY_GRID_END_HOUR, ADMIN_GRID_STATUS_OPTIONS, SLOT_STATUS_CLOSED, CLOSE_REASON_PROMPT_CONFIG_KEY } from '../constants/bookingConst';
import { getEnabledGridStatuses, isCloseReasonPromptEnabled } from '../utils/bookingUtils';
// Overlaps between day-scoped, midnight-wrapping bands are close to impossible
// to spot by eye, so the schedule is checked per (day, hour) instead.
import { validateRateBands, ISSUE_LEVELS } from '../utils/rateBandValidationUtil';
// Images are downscaled and re-encoded before they go into `config` — every
// visitor downloads that whole node on page load, so an untouched 800KB photo
// was ~1.07MB of base64 pushed to every client. See imageCompressionUtil.js.
import { compressImage, IMAGE_PRESETS } from '../utils/imageCompressionUtil';
import Modal from './Modal';
// Warns the admin when Shared Ownership is on but a court has no owner, or
// an owner has no QR — either of which makes courts unbookable for guests.
import OwnershipWarningBanner from './OwnershipWarningBanner';
import TimeSlotSetEditor from './TimeSlotSetEditor';
// Icon + colour for the separator each rate gets in the guest time slot grid.
import RateBandSeparatorSettings from './RateBandSeparatorSettings';
import ColorField from './ColorField';
// Config -> Theme -> Time Slot Button Colors (guest grid slot buttons).
import SlotButtonColorSettings from './SlotButtonColorSettings';
// Config -> Theme -> UI Design (which guest booking screen design renders).
import UiDesignSettings from './UiDesignSettings';
// Config -> Theme -> Open in Browser Popup (which popup Facebook guests see).
import BrowserPromptSettings from './BrowserPromptSettings';
// Config -> Sports & Courts -> Hourly rates: rate separators (show/hide + design).
import RateSeparatorDisplaySettings from './RateSeparatorDisplaySettings';
import { isRateSeparatorShown, resolveRateSeparatorDesign, describeRateSeparatorSetting } from '../utils/rateSeparatorUtil';
// Config -> Booking Settings (minimum hours, connecting rules, block bookings).
import BookingSettingsTab from './BookingSettingsTab';
// Config -> Court Reminders preview, drawn the way the guest popup draws it.
import FormattedText from './FormattedText';
import { describeBookingSettingsChanges, getBookingSettings } from '../utils/bookingRulesUtil';
import { getUiDesignLabel } from '../utils/uiDesignUtil';
import { getBrowserPromptLabel } from '../utils/browserPromptUtil';
import { listChangedSlotStatuses } from '../utils/slotButtonColorUtil';
import { logAuditEvent } from '../utils/auditLogUtil';
import {
  ADMIN_COLORS,
  AUDIT_ACTION_TYPES,
  ENTITY_TYPES,
  SLOT_BUTTON_COLORS_CONFIG_KEY,
  SHOW_COURT_RULES_CONFIG_KEY,
  COURT_REMINDERS_TEXT_CONFIG_KEY,
  COURT_REMINDERS_TEXT_MAX_LENGTH,
  COURT_REMINDERS_TAB_ID,
  COURT_REMINDERS_TAB_LABEL,
  UI_DESIGN_CONFIG_KEY,
  DEFAULT_UI_DESIGN_VERSION,
  BROWSER_PROMPT_CONFIG_KEY,
  RATE_SEPARATOR_SHOW_CONFIG_KEY,
  RATE_SEPARATOR_DESIGN_CONFIG_KEY,
  DEFAULT_BROWSER_PROMPT_VERSION,
  BROWSER_PROMPT_AUDIT_SETTING_NAME,
  BOOKING_SETTINGS_CONFIG_KEY,
  ADMIN_IDLE_TIMEOUT_MS,
  DEFAULT_BOOKING_WINDOW_DAYS,
  MIN_BOOKING_WINDOW_DAYS,
  MAX_BOOKING_WINDOW_DAYS,
  DEFAULT_CURRENCY_SYMBOL,
  DEFAULT_CURRENCY_DESCRIPTION,
  DEFAULT_PARTIAL_PAYMENT_PERCENT,
  MAX_PARTIAL_PAYMENT_PERCENT,
} from '../constants/bookingConst';
// The Pricing tab's partial-payment field reads back through the same
// helpers the admin booking screen enforces with, so the worked example
// under the field can never disagree with what the button will accept.
import { getPartialPaymentPercent, getMinimumPartialAmount } from '../utils/partialPaymentUtil';
// Every amount on this screen prints through formatMoney, and every price
// field parses through parsePriceInput, so the club's configured symbol and
// decimal places apply here exactly as they do on the guest side.
import { formatMoney, getCurrencySymbol, parsePriceInput } from '../utils/currencyUtil';
import '../css/AdminConfig.css';

// Shared jassen light palette (constants/bookingConst.js)
const COLORS = ADMIN_COLORS;

// Site-wide color fields (Theme tab) — each maps to a config key that
// ConfigContext.jsx feeds into a dynamically-injected stylesheet, so
// saving these here re-colors the whole site (see ConfigContext.jsx).
const THEME_COLOR_FIELDS = [
  { key: 'themeTextColor', label: 'Text Color', hint: 'Main body & heading text (usually dark)' },
  { key: 'themeBackgroundColor', label: 'Screen Background', hint: 'Page background behind all content' },
  { key: 'themeHeaderBackgroundColor', label: 'Header Background', hint: 'Top navigation bar background' },
  { key: 'themeActiveColor', label: 'Active Button Color', hint: 'Primary buttons, active tabs/steps, focus outlines' },
  { key: 'themeBrandColor', label: 'Court Name / Logo Color', hint: 'Court name text in the header and logo areas' },
];

const DEFAULT_THEME_COLORS = {
  themeTextColor: '#3d1220',
  themeBackgroundColor: '#f1efe9',
  themeHeaderBackgroundColor: '#ffffff',
  themeActiveColor: '#6fb5ab',
  themeBrandColor: '#3d1220',
};

// A court card is three stacked rows — identity, rate overrides, toggles —
// rather than one long wrapping line. With a per-band override box per rate
// band the single row wrapped at an arbitrary point, splitting "Available"
// and "Booked label" away from the fields they belong with and leaving the
// labels on different lines; grouping by purpose keeps each row readable no
// matter how many bands a sport has.
const COURT_CARD_ROW = {
  // flex-start, not center: a text input is taller than a checkbox, and
  // centring them staggered every label to a different vertical position.
  display: 'flex', alignItems: 'flex-start', gap: '0.6rem', flexWrap: 'wrap',
};

// Shared look for the System tab's toggle cards, so a new setting can't
// drift a few pixels away from the ones already there.
const SYSTEM_TOGGLE_CARD = {
  display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.85rem',
  border: `1px solid ${COLORS.line}`, borderRadius: '10px', cursor: 'pointer', maxWidth: '480px',
};
const SYSTEM_TOGGLE_BOX = {
  width: '18px', height: '18px', marginTop: '2px', flexShrink: 0, cursor: 'pointer',
};
// Same card as the toggles above, for a System setting that takes a value
// instead of a switch — no checkbox column, so the copy runs full width and
// the whole card isn't a click target.
const SYSTEM_FIELD_CARD = {
  padding: '0.85rem', border: `1px solid ${COLORS.line}`, borderRadius: '10px', maxWidth: '480px',
};

/**
 * Keeps the advance-booking-window field inside its allowed range as it's
 * typed, so an out-of-range value can't be saved at all — the input's own
 * min/max only style the spinner, they don't stop a typed or pasted number.
 * A cleared field falls back to the default rather than to NaN.
 *
 * @param {string} value - the raw input value
 * @returns {number} whole days within [MIN_, MAX_BOOKING_WINDOW_DAYS]
 */
const clampBookingWindowDays = (value) => {
  const parsed = parseInt(value, 10);
  if (!Number.isFinite(parsed)) return DEFAULT_BOOKING_WINDOW_DAYS;
  return Math.min(Math.max(parsed, MIN_BOOKING_WINDOW_DAYS), MAX_BOOKING_WINDOW_DAYS);
};

// Shown in the System tab's copy so the stated timeout can't drift from the
// one authServiceUtil.js actually enforces.
const ADMIN_IDLE_TIMEOUT_MINUTES = Math.round(ADMIN_IDLE_TIMEOUT_MS / 60000);

function AdminConfig() {
  const {
    config, loading: configLoading, updateConfig, addQRCode, removeQRCode, addGalleryPhoto, removeGalleryPhoto,
    addSportGalleryPhoto, removeSportGalleryPhoto, addLandingImage, removeLandingImage,
    addAboutImage, removeAboutImage,
  } = useConfig();
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [qrImageFile, setQrImageFile] = useState(null);
  const [qrName, setQrName] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [uploadingQR, setUploadingQR] = useState(false);
  // Which owner's QR is mid-upload (Config -> Payment -> Court Owners),
  // keyed by owner id since every owner card has its own upload button.
  const [uploadingOwnerQrId, setUploadingOwnerQrId] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingLandingPhoto, setUploadingLandingPhoto] = useState(false);
  const [uploadingAboutPhoto, setUploadingAboutPhoto] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  // Which sport's "Pick your court" card image is currently uploading (see
  // handleUploadSportImage) — keyed by sportId since multiple sport cards
  // each have their own independent upload button.
  const [uploadingSportImageId, setUploadingSportImageId] = useState(null);
  // Which sport's booking-screen photo gallery (see handleAddSportGalleryPhoto)
  // is currently uploading — separate from uploadingSportImageId above,
  // since that's a single replace-in-place image and this is an add-to-list.
  const [uploadingSportGalleryId, setUploadingSportGalleryId] = useState(null);

  const [modalConfig, setModalConfig] = useState({
    isOpen: false, title: '', message: '', type: 'info', confirmText: 'OK', onConfirm: null
  });

  // Raw text backing the comma-separated Service tags field. Kept separate
  // from the parsed array because deriving the textarea's value from
  // serviceTags.join(', ') meant every keystroke was split → trimmed →
  // rejoined, which deleted spaces and commas the moment they were typed
  // ("Court " became "Court", and ", " vanished entirely). The text is
  // what the admin sees; the array is just what gets saved.
  const [serviceTagsText, setServiceTagsText] = useState('');
  // Same raw-text treatment for the hero pills field.
  const [heroTagsText, setHeroTagsText] = useState('');

  // Initialize form data when config loads
  useEffect(() => {
    if (!configLoading && config) {
      setFormData({ ...config });
      setServiceTagsText((config.serviceTags || []).join(', '));
      setHeroTagsText((config.heroTags || []).join(', '));
    }
  }, [configLoading, config]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  /**
   * Ticks or unticks one status in Config -> System -> "Availability status
   * options". Saves the WHOLE map (every option's current state), so a
   * default the admin never touched is pinned as displayed rather than
   * shifting if a later build changes that default.
   *
   * @param {string} status - one of ADMIN_GRID_STATUS_OPTIONS[].value
   * @param {boolean} checked
   * @returns {void}
   */
  const handleGridStatusToggle = (status, checked) => {
    setFormData(prev => {
      const enabled = getEnabledGridStatuses(prev);
      const next = {};
      ADMIN_GRID_STATUS_OPTIONS.forEach(opt => { next[opt.value] = enabled.has(opt.value); });
      next[status] = checked;
      return { ...prev, adminGridStatusOptions: next };
    });
  };

  // Config -> System -> Advance booking window. Resolved through the same
  // helper the guest screen uses, so the last bookable date quoted in the
  // hint is the one guests will actually see rather than a second
  // calculation that could drift from it.
  const bookingWindowDays = getBookingWindowDays(formData);
  const bookingWindowPreview = useMemo(() => {
    const last = new Date();
    last.setDate(last.getDate() + bookingWindowDays);
    return last.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }, [bookingWindowDays]);

  const showModal = (config) => setModalConfig({ isOpen: true, ...config });
  const closeModal = () => setModalConfig(prev => ({ ...prev, isOpen: false }));

  const handleSave = async () => {
    // A block whose end is not after its start would be dropped silently by
    // getBookingSettings — refuse the save instead, so the admin sees the
    // block they typed rather than finding it gone later.
    const rawBlocks = Object.values(formData[BOOKING_SETTINGS_CONFIG_KEY]?.blockBookings || {}).filter(Boolean);
    if (rawBlocks.length !== getBookingSettings(formData).blockBookings.length) {
      setActiveTab('booking');
      showModal({
        title: 'Check Block Bookings',
        message: 'A block booking ends at or before its start time. Fix its hours (or remove it) under Booking Settings, then save again.',
        type: 'warning',
        confirmText: 'OK',
      });
      return;
    }
    setSaving(true);
    // Read before the save: once it lands, `config` already holds the new
    // colours and there would be nothing left to compare against.
    const changedSlotStatuses = listChangedSlotStatuses(
      config?.[SLOT_BUTTON_COLORS_CONFIG_KEY],
      formData[SLOT_BUTTON_COLORS_CONFIG_KEY],
    );
    // Same reason: compare the UI design against what was saved before.
    const previousUiDesign = config?.[UI_DESIGN_CONFIG_KEY] || DEFAULT_UI_DESIGN_VERSION;
    const nextUiDesign = formData[UI_DESIGN_CONFIG_KEY] || DEFAULT_UI_DESIGN_VERSION;
    // And the Open in Browser popup version, for the same reason.
    const previousBrowserPrompt = config?.[BROWSER_PROMPT_CONFIG_KEY] || DEFAULT_BROWSER_PROMPT_VERSION;
    const nextBrowserPrompt = formData[BROWSER_PROMPT_CONFIG_KEY] || DEFAULT_BROWSER_PROMPT_VERSION;
    // And the Booking Settings tab, compared before the save for the same reason.
    const bookingSettingsChanges = describeBookingSettingsChanges(config, formData);
    // And the close-reason prompt toggle, for the same reason.
    const previousAskCloseReason = isCloseReasonPromptEnabled(config);
    const nextAskCloseReason = isCloseReasonPromptEnabled(formData);
    // And the rate separator display, for the same reason.
    const previousRateSeparators = describeRateSeparatorSetting(config);
    const nextRateSeparators = describeRateSeparatorSetting(formData);
    try {
      await updateConfig({ ...formData, ...deriveLegacyCourtFields(formData) });
      if (changedSlotStatuses.length > 0) {
        const isReset = Object.keys(formData[SLOT_BUTTON_COLORS_CONFIG_KEY] || {}).length === 0;
        // Never throws — a failed audit write must not report the save as failed.
        logAuditEvent({
          actionType: AUDIT_ACTION_TYPES.SLOT_COLORS_UPDATED,
          entityType: ENTITY_TYPES.CONFIG,
          entityId: SLOT_BUTTON_COLORS_CONFIG_KEY,
          summary: { type: 'slotColors', statuses: changedSlotStatuses.join(', '), reset: isReset },
          changes: {
            before: config?.[SLOT_BUTTON_COLORS_CONFIG_KEY] || null,
            after: isReset ? null : formData[SLOT_BUTTON_COLORS_CONFIG_KEY],
          },
        });
      }
      if (previousUiDesign !== nextUiDesign) {
        // Never throws — a failed audit write must not report the save as failed.
        logAuditEvent({
          actionType: AUDIT_ACTION_TYPES.UI_DESIGN_UPDATED,
          entityType: ENTITY_TYPES.CONFIG,
          entityId: UI_DESIGN_CONFIG_KEY,
          summary: {
            type: 'uiDesign',
            from: getUiDesignLabel(previousUiDesign),
            to: getUiDesignLabel(nextUiDesign),
          },
          changes: { before: previousUiDesign, after: nextUiDesign },
        });
      }
      if (bookingSettingsChanges.length > 0) {
        // Never throws — a failed audit write must not report the save as failed.
        logAuditEvent({
          actionType: AUDIT_ACTION_TYPES.BOOKING_SETTINGS_UPDATED,
          entityType: ENTITY_TYPES.CONFIG,
          entityId: BOOKING_SETTINGS_CONFIG_KEY,
          summary: { type: 'bookingSettings', changes: bookingSettingsChanges.join(' · ') },
          changes: {
            before: config?.[BOOKING_SETTINGS_CONFIG_KEY] || null,
            after: formData[BOOKING_SETTINGS_CONFIG_KEY] || null,
          },
        });
      }
      if (previousRateSeparators !== nextRateSeparators) {
        // Never throws — a failed audit write must not report the save as failed.
        logAuditEvent({
          actionType: AUDIT_ACTION_TYPES.RATE_SEPARATORS_UPDATED,
          entityType: ENTITY_TYPES.CONFIG,
          entityId: RATE_SEPARATOR_DESIGN_CONFIG_KEY,
          summary: {
            type: 'settingToggle',
            setting: 'Rate separators',
            from: previousRateSeparators,
            to: nextRateSeparators,
          },
          changes: {
            before: {
              show: isRateSeparatorShown(config),
              design: resolveRateSeparatorDesign(config),
            },
            after: {
              show: isRateSeparatorShown(formData),
              design: resolveRateSeparatorDesign(formData),
            },
          },
        });
      }
      if (previousAskCloseReason !== nextAskCloseReason) {
        // Never throws — a failed audit write must not report the save as failed.
        logAuditEvent({
          actionType: AUDIT_ACTION_TYPES.CLOSE_REASON_PROMPT_UPDATED,
          entityType: ENTITY_TYPES.CONFIG,
          entityId: CLOSE_REASON_PROMPT_CONFIG_KEY,
          summary: {
            type: 'settingToggle',
            setting: 'Ask reason for closing timeslot',
            from: previousAskCloseReason ? 'On' : 'Off',
            to: nextAskCloseReason ? 'On' : 'Off',
          },
          changes: { before: previousAskCloseReason, after: nextAskCloseReason },
        });
      }
      if (previousBrowserPrompt !== nextBrowserPrompt) {
        // Never throws — a failed audit write must not report the save as failed.
        // 'settingToggle' reuses the audit trail's "Setting: from → to" line.
        logAuditEvent({
          actionType: AUDIT_ACTION_TYPES.BROWSER_PROMPT_UPDATED,
          entityType: ENTITY_TYPES.CONFIG,
          entityId: BROWSER_PROMPT_CONFIG_KEY,
          summary: {
            type: 'settingToggle',
            setting: BROWSER_PROMPT_AUDIT_SETTING_NAME,
            from: getBrowserPromptLabel(previousBrowserPrompt),
            to: getBrowserPromptLabel(nextBrowserPrompt),
          },
          changes: { before: previousBrowserPrompt, after: nextBrowserPrompt },
        });
      }
      showModal({
        title: 'Success',
        message: 'Configuration saved successfully!',
        type: 'success',
        confirmText: 'OK',
        onConfirm: closeModal
      });
    } catch (error) {
      showModal({
        title: 'Error',
        message: 'Failed to save configuration: ' + error.message,
        type: 'error',
        confirmText: 'OK'
      });
    }
    setSaving(false);
  };

  // Slots are kept sorted in formData so the stored order always matches the
  // order rendered below. They used to be sorted by a private comparator that
  // read the AM/PM suffix as the START hour's meridiem — which put "11-12PM"
  // (11 AM to noon) at the very end of the day instead of before "12-1PM".
  // The list was then rendered through a SECOND, differently-buggy private
  // sort, so the position clicked and the position stored disagreed and
  // removing a slot deleted a different one. Both are gone; this is now the
  // same sortTimeSlots the booking grid uses.
  // ---- Time-slot sets --------------------------------------------------
  //
  // `timeSlots` stays exactly what it always was: the DEFAULT hours. A set
  // in `timeSlotSets` overrides them on the weekdays it claims, which is how
  // a club that closes early at weekends is expressed. Adding sets needs no
  // migration, and a deployment with none behaves as it always has — see
  // getTimeSlotsForDate in bookingUtils.js for the resolution order.
  //
  // Both editors go through TimeSlotSetEditor, which sorts on every change,
  // so stored order always matches rendered order. That is what the old
  // single list got wrong: it sorted for display with one comparator and
  // stored with another, so removing a chip deleted a different slot.

  /** A new set, pre-filled with the default hours so it is edited down rather than built up. */
  const handleAddSlotSet = () => {
    const next = [...(formData.timeSlotSets || []), {
      id: `ts-${Date.now()}`,
      label: '',
      days: [],
      // Left empty on purpose, which reads as "every court" (see
      // getSlotSetCourts). The admin narrows it down if they want to; a set
      // that applies everywhere is by far the common case and should not
      // need any ticking to get there.
      courts: [],
      // Copied, not shared: the point of a set is usually "the same day but
      // shorter", and starting from an empty list means retyping eighteen
      // hours to remove two.
      slots: [...(formData.timeSlots || [])],
    }];
    handleChange('timeSlotSets', next);
  };

  /**
   * Patches one set in place.
   * @param {string} id
   * @param {object} patch - any of { label, days, slots }
   */
  const handleUpdateSlotSet = (id, patch) => {
    handleChange('timeSlotSets', (formData.timeSlotSets || []).map(set => (
      set.id === id ? { ...set, ...patch } : set
    )));
  };

  /** @param {string} id */
  const handleRemoveSlotSet = (id) => {
    handleChange('timeSlotSets', (formData.timeSlotSets || []).filter(set => set.id !== id));
  };

  // Weekdays claimed by more than one set. Surfaced in the editor rather
  // than silently resolved, since the losing set looks configured but does
  // nothing.
  // Every configured court, for the per-set court pickers. Labelled with the
  // sport because sets are club-wide while courts are not: "Court 3" alone
  // is ambiguous the moment a club runs two sports.
  const slotSetCourtOptions = useMemo(() => (
    (formData.courts || [])
      .map((court) => {
        const number = Number(court?.number);
        if (!Number.isFinite(number)) return null;
        const sportName = (formData.sports || []).find(s => s.id === court.sportId)?.name;
        const name = court.name || `Court ${number}`;
        return { number, label: sportName ? `${name} (${sportName})` : name };
      })
      .filter(Boolean)
      .sort((a, b) => a.number - b.number)
  ), [formData.courts, formData.sports]);

  // A weekday in two sets is only a clash when those sets also share a
  // court: since sets are court-scoped, "Sat courts 1-2" and "Sat court 3"
  // are the intended way to give one day two different sets of hours.
  const duplicateSlotSetDays = useMemo(() => {
    const claimedBy = {};
    const dupes = new Set();
    (formData.timeSlotSets || []).forEach((set) => {
      const scoped = (Array.isArray(set?.courts) ? set.courts : []).map(Number).filter(Number.isFinite);
      // An unscoped set covers every court, so it clashes with any other set
      // on the same day whatever that one is scoped to.
      const courts = scoped.length > 0 ? scoped : slotSetCourtOptions.map(c => c.number);
      (Array.isArray(set?.days) ? set.days : []).forEach((day) => {
        claimedBy[day] = claimedBy[day] || new Set();
        courts.forEach((court) => {
          if (claimedBy[day].has(court)) dupes.add(day);
          claimedBy[day].add(court);
        });
      });
    });
    return [...dupes].sort((a, b) => a - b).map(d => WEEKDAYS[d]?.label).filter(Boolean);
  }, [formData.timeSlotSets, slotSetCourtOptions]);


  // --- Rate bands (Config -> Sports & Courts) ---
  // A sport's day is split into named price bands instead of the old fixed
  // off-peak/peak pair, so a club can charge e.g. 6AM-3PM ₱300, 3PM-5PM ₱400,
  // 5PM-6AM ₱450. startHour is inclusive, endHour exclusive, and a band whose
  // end is at or before its start wraps past midnight.

  /** Replaces one sport's whole band list. */
  const setSportBands = (sportId, rateBands) => {
    handleChange('sports', (formData.sports || []).map(
      s => (s.id === sportId ? { ...s, rateBands } : s)
    ));
  };

  /** Edits one field of one band. */
  const updateBand = (sportId, bandId, field, value) => {
    const sport = (formData.sports || []).find(s => s.id === sportId);
    setSportBands(sportId, (sport?.rateBands || []).map(
      b => (b.id === bandId ? { ...b, [field]: value } : b)
    ));
  };

  /**
   * Appends a band starting where the last one ends, so consecutive bands
   * chain without the admin having to retype the boundary hour.
   */
  const handleAddBand = (sportId) => {
    const sport = (formData.sports || []).find(s => s.id === sportId);
    const bands = sport?.rateBands || [];
    const last = bands[bands.length - 1];
    const startHour = last ? Number(last.endHour) || 0 : 6;
    setSportBands(sportId, [...bands, {
      id: `band-${Date.now()}`,
      name: '',
      startHour,
      endHour: (startHour + 1) % 24,
      price: Number(last?.price) || 0,
      // Empty = every day. A new band shouldn't silently apply to fewer days
      // than the admin expects, so it starts unscoped.
      days: [],
    }]);
  };

  /**
   * Toggles one weekday on a band. Ticking all seven clears the list back to
   * [] so the stored config keeps saying "every day" the one way the rest of
   * the app reads it (see bandAppliesOnDay in bookingUtils.js).
   */
  const toggleBandDay = (sportId, bandId, dayIndex) => {
    const sport = (formData.sports || []).find(s => s.id === sportId);
    const band = (sport?.rateBands || []).find(b => b.id === bandId);
    if (!band) return;
    // A band with no days set applies every day, so the first click has to
    // start from "all seven selected" and remove one — otherwise unticking
    // Sunday would leave the band on Sunday alone.
    const current = Array.isArray(band.days) && band.days.length > 0
      ? band.days.map(Number)
      : [0, 1, 2, 3, 4, 5, 6];
    const next = current.includes(dayIndex)
      ? current.filter(d => d !== dayIndex)
      : [...current, dayIndex].sort((a, b) => a - b);
    updateBand(sportId, bandId, 'days', next.length === 7 ? [] : next);
  };

  /** Whether a day chip should render as selected. */
  const isBandDayOn = (band, dayIndex) => {
    const days = band?.days;
    return !Array.isArray(days) || days.length === 0 || days.map(Number).includes(dayIndex);
  };

  /** Removes a band, and any per-court override that pointed at it. */
  const handleRemoveBand = (sportId, bandId) => {
    const sport = (formData.sports || []).find(s => s.id === sportId);
    setSportBands(sportId, (sport?.rateBands || []).filter(b => b.id !== bandId));
    handleChange('courts', (formData.courts || []).map((court) => {
      if (!court.bandRates || court.bandRates[bandId] === undefined) return court;
      const { [bandId]: _removed, ...rest } = court.bandRates;
      return { ...court, bandRates: rest };
    }));
  };

  /** Sets or clears one court's override for one band ('' = inherit). */
  const updateCourtBandRate = (courtId, bandId, value) => {
    handleChange('courts', (formData.courts || []).map((court) => {
      if (court.id !== courtId) return court;
      const bandRates = { ...(court.bandRates || {}) };
      if (value === '') delete bandRates[bandId];
      else bandRates[bandId] = value;
      return { ...court, bandRates };
    }));
  };

  /**
   * Conflicts and gaps in one sport's schedule. Surfaced rather than blocked —
   * an admin mid-edit will often have a temporary gap, and getCourtRate falls
   * back to the cheapest band anyway. See rateBandValidationUtil.js for what
   * each issue means.
   * @param {object} sport
   * @returns {Array<{level: string, code: string, message: string}>}
   */
  // Validated against EVERY configured hour, not just the default list: a
  // band covering an hour that only exists in a weekend set is legitimate,
  // and checking it against the default day alone would flag it as covering
  // nothing.
  const bandIssues = (sport) => validateRateBands(sport?.rateBands, { timeSlots: getAllConfiguredTimeSlots(formData), config: formData });

  // --- Sports & Courts (Config -> Sports & Courts tab) ---
  // Sports/courts are plain arrays on formData, edited locally like every
  // other tab here and persisted together on Save. See ConfigContext.jsx
  // for the sports[]/courts[] shape and the legacy numberOfCourts/
  // pricePeak/priceOffPeak/peakRateStart/courtRates fields these are
  // mirrored into on save (deriveLegacyCourtFields below) so the booking
  // flow — not yet made sport-aware — keeps reading correct values from
  // the first configured sport and the full court list.

  const updateSport = (sportId, field, value) => {
    const sports = (formData.sports || []).map(s => (s.id === sportId ? { ...s, [field]: value } : s));
    handleChange('sports', sports);
  };

  const handleAddSport = () => {
    const sports = formData.sports || [];
    const base = sports[0] || { peakRateStart: 16, pricePeak: 300, priceOffPeak: 200 };
    const newSport = {
      id: `sport-${Date.now()}`,
      name: 'New Sport',
      icon: '🎾',
      peakRateStart: base.peakRateStart,
      pricePeak: base.pricePeak,
      priceOffPeak: base.priceOffPeak,
      openPlayEnabled: false,
      openPlayPricePerPlayer: 0,
      openPlayNote: '',
    };
    handleChange('sports', [...sports, newSport]);
  };

  const handleRemoveSport = (sportId) => {
    const sports = formData.sports || [];
    if (sports.length <= 1) return; // always keep at least one sport
    showModal({
      title: 'Remove Sport',
      message: 'Courts assigned to this sport will need to be reassigned. Remove this sport?',
      type: 'warning',
      confirmText: 'Remove',
      showCancel: true,
      cancelText: 'Cancel',
      onConfirm: () => {
        const remaining = sports.filter(s => s.id !== sportId);
        handleChange('sports', remaining);
        // Reassign any orphaned courts to the first remaining sport so
        // every court always has a valid sportId.
        const fallbackId = remaining[0]?.id;
        const courts = (formData.courts || []).map(c => (c.sportId === sportId ? { ...c, sportId: fallbackId } : c));
        handleChange('courts', courts);
        closeModal();
      },
    });
  };

  const updateCourt = (courtId, field, value) => {
    const courts = (formData.courts || []).map(c => (c.id === courtId ? { ...c, [field]: value } : c));
    handleChange('courts', courts);
  };

  /**
   * Court type spans two stored fields — `indoor` (true/false/null) for a real
   * court and `isAddOn` for the ball-machine-style extras — so switching type
   * has to clear whichever one no longer applies. Done in a single update so a
   * court can never end up flagged indoor AND add-on at once.
   *
   * @param {string} courtId
   * @param {'indoor'|'outdoor'|'addon'|''} type - the dropdown's value
   */
  const updateCourtType = (courtId, type) => {
    handleChange('courts', (formData.courts || []).map((court) => {
      if (court.id !== courtId) return court;
      if (type === 'addon') {
        return {
          ...court,
          indoor: null,
          isAddOn: true,
          // Seeded so the pairing/message editor below always has something
          // defined to read, rather than rendering off undefined.
          addOnPairedCourts: Array.isArray(court.addOnPairedCourts) ? court.addOnPairedCourts : [],
          addOnRequiredMessage: court.addOnRequiredMessage || '',
        };
      }
      return {
        ...court,
        isAddOn: false,
        indoor: type === 'indoor' ? true : type === 'outdoor' ? false : null,
      };
    }));
  };

  /**
   * Ticks/unticks one real court in an add-on's "Pairs with" list.
   * @param {string} courtId - the ADD-ON's id
   * @param {number} pairedNumber - the real court's number
   */
  const toggleAddOnPairedCourt = (courtId, pairedNumber) => {
    handleChange('courts', (formData.courts || []).map((court) => {
      if (court.id !== courtId) return court;
      const current = (Array.isArray(court.addOnPairedCourts) ? court.addOnPairedCourts : []).map(Number);
      const number = Number(pairedNumber);
      const next = current.includes(number)
        ? current.filter(n => n !== number)
        : [...current, number].sort((a, b) => a - b);
      return { ...court, addOnPairedCourts: next };
    }));
  };

  /**
   * The real courts an add-on may be paired with: same sport, not itself an
   * add-on. Pairing an add-on to another add-on would let a guest hold a ball
   * machine and a coaching lane with no actual court booked.
   *
   * @param {object} addOn - the add-on court record
   * @returns {object[]} court records, in court-number order
   */
  const pairableCourtsFor = (addOn) => (formData.courts || [])
    .filter(c => c.isAddOn !== true && c.sportId === addOn.sportId)
    .sort((a, b) => Number(a.number) - Number(b.number));

  const handleAddCourt = () => {
    const courts = formData.courts || [];
    const nextNumber = courts.reduce((max, c) => Math.max(max, Number(c.number) || 0), 0) + 1;
    const defaultSportId = formData.sports?.[0]?.id || 'default';
    const newCourt = {
      id: `court-${Date.now()}`,
      number: nextNumber,
      // Optional display name — blank falls back to "Court {number}"
      // wherever this court is shown (see getCourtLabel in bookingUtils.js).
      name: '',
      sportId: defaultSportId,
      // Untick to temporarily close this court (maintenance/resurfacing) —
      // it stays on the guest grid but every hour renders unbookable.
      available: true,
      indoor: null,
      // Court type "Add-on" — a ball machine or similar, bookable only on an
      // hour the guest already has one of addOnPairedCourts for. See
      // updateCourtType and the add-on helpers in bookingUtils.js.
      isAddOn: false,
      addOnPairedCourts: [],
      addOnRequiredMessage: '',
      dayRate: null,
      nightRate: null,
      showBookedInitials: false,
    };
    handleChange('courts', [...courts, newCourt]);
  };

  // ---- Court Details cards (free-form title/description list) ----
  const updateCourtDetail = (detailId, field, value) => {
    const courtDetails = (formData.courtDetails || []).map(d => (d.id === detailId ? { ...d, [field]: value } : d));
    handleChange('courtDetails', courtDetails);
  };

  const handleAddCourtDetail = () => {
    const newDetail = { id: `detail-${Date.now()}`, title: '', description: '' };
    handleChange('courtDetails', [...(formData.courtDetails || []), newDetail]);
  };

  const handleRemoveCourtDetail = (detailId) => {
    handleChange('courtDetails', (formData.courtDetails || []).filter(d => d.id !== detailId));
  };

  // Cards render to guests in list order, so the admin needs a way to
  // reorder them without deleting and retyping.
  const moveCourtDetail = (detailId, direction) => {
    const details = [...(formData.courtDetails || [])];
    const index = details.findIndex(d => d.id === detailId);
    const target = index + direction;
    if (index === -1 || target < 0 || target >= details.length) return;
    [details[index], details[target]] = [details[target], details[index]];
    handleChange('courtDetails', details);
  };

  // ---- Policies (Config -> Policies) ----
  // Same card shape and interactions as courtDetails above — they replaced
  // five fixed rule fields so a club can add or drop rules freely.
  const updatePolicy = (policyId, field, value) => {
    handleChange('policies', (formData.policies || []).map(
      p => (p.id === policyId ? { ...p, [field]: value } : p)
    ));
  };

  const handleAddPolicy = () => {
    handleChange('policies', [
      ...(formData.policies || []),
      { id: `policy-${Date.now()}`, title: '', description: '' },
    ]);
  };

  const handleRemovePolicy = (policyId) => {
    handleChange('policies', (formData.policies || []).filter(p => p.id !== policyId));
  };

  const movePolicy = (policyId, direction) => {
    const policies = [...(formData.policies || [])];
    const index = policies.findIndex(p => p.id === policyId);
    const target = index + direction;
    if (index === -1 || target < 0 || target >= policies.length) return;
    [policies[index], policies[target]] = [policies[target], policies[index]];
    handleChange('policies', policies);
  };

  // ---- Services section (landing page) ----
  // Pills are edited as one comma-separated field but stored as an array,
  // so the landing page never has to parse display text. The raw text is
  // preserved verbatim in serviceTagsText — only the SAVED array is
  // trimmed/filtered, so typing a space or a comma isn't undone mid-edit.
  const handleServiceTagsChange = (value) => {
    setServiceTagsText(value);
    handleChange('serviceTags', value.split(',').map(s => s.trim()).filter(Boolean));
  };

  const handleHeroTagsChange = (value) => {
    setHeroTagsText(value);
    handleChange('heroTags', value.split(',').map(s => s.trim()).filter(Boolean));
  };

  const updateServiceCard = (cardId, field, value) => {
    const serviceCards = (formData.serviceCards || []).map(c => (c.id === cardId ? { ...c, [field]: value } : c));
    handleChange('serviceCards', serviceCards);
  };

  const handleAddServiceCard = () => {
    handleChange('serviceCards', [
      ...(formData.serviceCards || []),
      { id: `service-${Date.now()}`, title: '', description: '' },
    ]);
  };

  const handleRemoveServiceCard = (cardId) => {
    handleChange('serviceCards', (formData.serviceCards || []).filter(c => c.id !== cardId));
  };

  const moveServiceCard = (cardId, direction) => {
    const cards = [...(formData.serviceCards || [])];
    const index = cards.findIndex(c => c.id === cardId);
    const target = index + direction;
    if (index === -1 || target < 0 || target >= cards.length) return;
    [cards[index], cards[target]] = [cards[target], cards[index]];
    handleChange('serviceCards', cards);
  };

  // ---- FAQs (landing page accordion) ----
  const updateFaq = (faqId, field, value) => {
    handleChange('faqs', (formData.faqs || []).map(f => (f.id === faqId ? { ...f, [field]: value } : f)));
  };

  const handleAddFaq = () => {
    handleChange('faqs', [...(formData.faqs || []), { id: `faq-${Date.now()}`, question: '', answer: '' }]);
  };

  const handleRemoveFaq = (faqId) => {
    handleChange('faqs', (formData.faqs || []).filter(f => f.id !== faqId));
  };

  const moveFaq = (faqId, direction) => {
    const faqs = [...(formData.faqs || [])];
    const index = faqs.findIndex(f => f.id === faqId);
    const target = index + direction;
    if (index === -1 || target < 0 || target >= faqs.length) return;
    [faqs[index], faqs[target]] = [faqs[target], faqs[index]];
    handleChange('faqs', faqs);
  };

  // Copies the auto-generated list into the config so the admin can edit
  // real questions instead of starting from a blank row. Snapshots the
  // answers as they read right now — once loaded they stop tracking config
  // changes, which is the trade-off for being able to reword them.
  const handleLoadDefaultFaqs = () => {
    handleChange('faqs', buildDefaultFaqs(formData));
  };

  const handleRemoveCourt = (courtId) => {
    handleChange('courts', (formData.courts || []).filter(c => c.id !== courtId));
  };

  // --- Equipment / add-ons (Config -> Equipment tab) ---
  // Same array-on-formData pattern as sports/courts above. Each item is
  // assigned to a sport (config.equipment[].sportId) so a future
  // sport-scoped booking flow only offers e.g. rackets on the tennis
  // flow and paddles on the pickleball one — see EquipmentRentalModal's
  // optional sportId prop.

  const updateEquipmentItem = (itemId, field, value) => {
    const equipment = (formData.equipment || []).map(item => (item.id === itemId ? { ...item, [field]: value } : item));
    handleChange('equipment', equipment);
  };

  const handleAddEquipmentItem = () => {
    const equipment = formData.equipment || [];
    const defaultSportId = formData.sports?.[0]?.id || 'default';
    const newItem = {
      id: `equipment-${Date.now()}`,
      sportId: defaultSportId,
      label: 'New Item',
      unit: 'item',
      // 'session' (one charge per reservation) or 'hourly' (charged per
      // booked hour the guest keeps it) — see getEquipmentRentalType.
      rentalType: 'session',
      price: 0,
      inventoryTotal: 0,
    };
    handleChange('equipment', [...equipment, newItem]);
  };

  const handleRemoveEquipmentItem = (itemId) => {
    handleChange('equipment', (formData.equipment || []).filter(item => item.id !== itemId));
  };

  // Keeps the legacy single-sport fields (still read by the booking flow
  // and older admin screens) in sync with the first configured sport and
  // the full court list, so nothing breaks while those screens are
  // migrated to be sport-aware in a later phase.
  const deriveLegacyCourtFields = (data) => {
    const primarySport = (data.sports || [])[0];
    const courts = data.courts || [];
    const bands = primarySport?.rateBands || [];

    // The old two-rate fields are kept in sync as a LOSSY mirror: cheapest
    // band as off-peak, dearest as peak, and that dearest band's start as the
    // split. Three or more bands can't be represented faithfully, which is
    // the whole point of the new model — nothing prices off these anymore
    // (getCourtRate reads the bands), they exist only so a screen or an
    // integration still reading the flat fields shows something sane rather
    // than a stale number from before this save.
    const prices = bands.map(b => Number(b.price)).filter(Number.isFinite);
    const dearest = bands.length
      ? bands.reduce((a, b) => (Number(b.price) > Number(a.price) ? b : a))
      : null;
    const legacyRates = bands.length
      ? {
        priceOffPeak: Math.min(...prices),
        pricePeak: Math.max(...prices),
        peakRateStart: Number(dearest?.startHour) || 0,
      }
      : (primarySport ? {
        peakRateStart: primarySport.peakRateStart,
        pricePeak: primarySport.pricePeak,
        priceOffPeak: primarySport.priceOffPeak,
      } : {});

    // Same idea for per-court overrides: mirror each court's cheapest and
    // dearest band override into the legacy dayRate/nightRate pair.
    const courtRates = courts
      .filter(c => (c.bandRates && Object.keys(c.bandRates).length > 0) || c.dayRate != null || c.nightRate != null)
      .map((c) => {
        const overrides = Object.values(c.bandRates || {})
          .map(Number)
          .filter(Number.isFinite);
        return {
          court: c.number,
          dayRate: overrides.length ? Math.min(...overrides) : (c.dayRate ?? null),
          nightRate: overrides.length ? Math.max(...overrides) : (c.nightRate ?? null),
        };
      });

    return { numberOfCourts: courts.length, courtRates, ...legacyRates };
  };

  const handleAddQRCode = async () => {
    if (!qrImageFile || !qrName.trim()) {
      showModal({
        title: 'Missing Information',
        message: 'Please provide a name and select an image for the QR code.',
        type: 'warning',
        confirmText: 'OK'
      });
      return;
    }

    setUploadingQR(true);
    try {
      // Higher quality than a decorative photo: the QR modules have to stay
      // sharp enough for a phone camera to resolve them.
      const { dataUrl: base64Data } = await compressImage(qrImageFile, IMAGE_PRESETS.QR);

      await addQRCode(qrName.trim(), base64Data, accountName, accountNumber);

      setQrImageFile(null);
      setQrName('');
      setAccountName('');
      setAccountNumber('');
      showModal({
        title: 'Success',
        message: 'QR code added successfully!',
        type: 'success',
        confirmText: 'OK',
        onConfirm: closeModal
      });
    } catch (error) {
      showModal({
        title: 'Error',
        message: 'Failed to upload QR code: ' + error.message,
        type: 'error',
        confirmText: 'OK'
      });
    }
    setUploadingQR(false);
  };

  const handleAddGalleryPhoto = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showModal({ title: 'Invalid File', message: 'Please select an image file.', type: 'warning', confirmText: 'OK' });
      return;
    }
    setUploadingPhoto(true);
    try {
      const { dataUrl: base64Data } = await compressImage(file, IMAGE_PRESETS.PHOTO);
      await addGalleryPhoto(base64Data);
    } catch (error) {
      showModal({ title: 'Error', message: 'Failed to upload photo: ' + error.message, type: 'error', confirmText: 'OK' });
    }
    setUploadingPhoto(false);
  };

  const handleDeleteGalleryPhoto = (id) => {
    showModal({
      title: 'Delete Photo',
      message: 'Are you sure you want to remove this photo from the gallery?',
      type: 'warning',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await removeGalleryPhoto(id);
          closeModal();
        } catch (error) {
          showModal({ title: 'Error', message: 'Failed to delete photo: ' + error.message, type: 'error', confirmText: 'OK' });
        }
      },
      showCancel: true,
      cancelText: 'Cancel'
    });
  };

  // Per-sport gallery override (Config -> Photos, shown once there's more
  // than one sport) — mirrors handleAddGalleryPhoto/handleDeleteGalleryPhoto
  // above, just scoped to one sport via addSportGalleryPhoto/removeSportGalleryPhoto.
  const handleAddSportGalleryPhoto = async (sportId, e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showModal({ title: 'Invalid File', message: 'Please select an image file.', type: 'warning', confirmText: 'OK' });
      return;
    }
    setUploadingSportGalleryId(sportId);
    try {
      const { dataUrl: base64Data } = await compressImage(file, IMAGE_PRESETS.PHOTO);
      await addSportGalleryPhoto(sportId, base64Data);
    } catch (error) {
      showModal({ title: 'Error', message: 'Failed to upload photo: ' + error.message, type: 'error', confirmText: 'OK' });
    }
    setUploadingSportGalleryId(null);
  };

  const handleDeleteSportGalleryPhoto = (sportId, id) => {
    showModal({
      title: 'Delete Photo',
      message: 'Are you sure you want to remove this photo from this sport\'s gallery?',
      type: 'warning',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await removeSportGalleryPhoto(sportId, id);
          closeModal();
        } catch (error) {
          showModal({ title: 'Error', message: 'Failed to delete photo: ' + error.message, type: 'error', confirmText: 'OK' });
        }
      },
      showCancel: true,
      cancelText: 'Cancel'
    });
  };

  const handleAddLandingImage = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showModal({ title: 'Invalid File', message: 'Please select an image file.', type: 'warning', confirmText: 'OK' });
      return;
    }
    setUploadingLandingPhoto(true);
    try {
      const { dataUrl: base64Data } = await compressImage(file, IMAGE_PRESETS.PHOTO);
      await addLandingImage(base64Data);
    } catch (error) {
      showModal({ title: 'Error', message: 'Failed to upload photo: ' + error.message, type: 'error', confirmText: 'OK' });
    }
    setUploadingLandingPhoto(false);
  };

  // About-section photos — same 800KB base64 pipeline as the landing hero
  // uploads above, just writing into config.aboutImages.
  const handleAddAboutImage = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showModal({ title: 'Invalid File', message: 'Please select an image file.', type: 'warning', confirmText: 'OK' });
      return;
    }
    setUploadingAboutPhoto(true);
    try {
      const { dataUrl: base64Data } = await compressImage(file, IMAGE_PRESETS.PHOTO);
      await addAboutImage(base64Data);
    } catch (error) {
      showModal({ title: 'Error', message: 'Failed to upload photo: ' + error.message, type: 'error', confirmText: 'OK' });
    }
    setUploadingAboutPhoto(false);
  };

  const handleDeleteAboutImage = (id) => {
    showModal({
      title: 'Delete Photo',
      message: 'Remove this photo from the About section?',
      type: 'warning',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await removeAboutImage(id);
          closeModal();
        } catch (error) {
          showModal({ title: 'Error', message: 'Failed to delete photo: ' + error.message, type: 'error', confirmText: 'OK' });
        }
      },
      showCancel: true,
      cancelText: 'Cancel'
    });
  };

  const handleDeleteLandingImage = (id) => {
    showModal({
      title: 'Delete Photo',
      message: 'Are you sure you want to remove this photo from the landing page?',
      type: 'warning',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await removeLandingImage(id);
          closeModal();
        } catch (error) {
          showModal({ title: 'Error', message: 'Failed to delete photo: ' + error.message, type: 'error', confirmText: 'OK' });
        }
      },
      showCancel: true,
      cancelText: 'Cancel'
    });
  };

  const handleUploadLogo = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showModal({ title: 'Invalid File', message: 'Please select an image file.', type: 'warning', confirmText: 'OK' });
      return;
    }
    setUploadingLogo(true);
    try {
      const { dataUrl: base64Data } = await compressImage(file, IMAGE_PRESETS.LOGO);
      await updateConfig({ logoUrl: base64Data });
      handleChange('logoUrl', base64Data);
    } catch (error) {
      showModal({ title: 'Error', message: 'Failed to upload logo: ' + error.message, type: 'error', confirmText: 'OK' });
    }
    setUploadingLogo(false);
  };

  const handleRemoveLogo = async () => {
    try {
      await updateConfig({ logoUrl: '' });
      handleChange('logoUrl', '');
    } catch (error) {
      showModal({ title: 'Error', message: 'Failed to remove logo: ' + error.message, type: 'error', confirmText: 'OK' });
    }
  };

  // "Pick your court" sport card image (Config -> Landing Page) — one image
  // per sport, uploaded/replaced/removed the same way the club logo is
  // (immediate Firebase write via updateConfig, not gated behind Save), so
  // the admin sees the change take effect right away. Distinct from the
  // shared hero gallery (landingImages), which isn't sport-specific.
  const handleUploadSportImage = async (sportId, e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showModal({ title: 'Invalid File', message: 'Please select an image file.', type: 'warning', confirmText: 'OK' });
      return;
    }
    setUploadingSportImageId(sportId);
    try {
      const { dataUrl: base64Data } = await compressImage(file, IMAGE_PRESETS.PHOTO);
      const sports = (formData.sports || []).map(s => (s.id === sportId ? { ...s, landingImageUrl: base64Data } : s));
      await updateConfig({ sports });
      handleChange('sports', sports);
    } catch (error) {
      showModal({ title: 'Error', message: 'Failed to upload photo: ' + error.message, type: 'error', confirmText: 'OK' });
    }
    setUploadingSportImageId(null);
  };

  const handleRemoveSportImage = async (sportId) => {
    try {
      const sports = (formData.sports || []).map(s => (s.id === sportId ? { ...s, landingImageUrl: '' } : s));
      await updateConfig({ sports });
      handleChange('sports', sports);
    } catch (error) {
      showModal({ title: 'Error', message: 'Failed to remove photo: ' + error.message, type: 'error', confirmText: 'OK' });
    }
  };

  const handleDeleteQRCode = async (id, url) => {
    showModal({
      title: 'Delete QR Code',
      message: 'Are you sure you want to delete this QR code?',
      type: 'warning',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await removeQRCode(id);
          closeModal();
        } catch (error) {
          showModal({
            title: 'Error',
            message: 'Failed to delete QR code: ' + error.message,
            type: 'error',
            confirmText: 'OK'
          });
        }
      },
      showCancel: true,
      cancelText: 'Cancel'
    });
  };

  // ---- Court owners (Config -> Payment, shared ownership only) ----
  // Unlike the club-wide QR list above — which writes straight to Firebase
  // through addQRCode/removeQRCode — owners live in `formData` and are
  // persisted by the Save button at the bottom of the screen, the same way
  // sports and courts already work. That matters here: an owner's name,
  // contact and QR are edited together, and an immediate write on the QR
  // upload would re-sync formData from config (see the useEffect above)
  // and wipe the name the admin was halfway through typing.
  const updateOwner = (ownerId, field, value) => {
    const owners = (formData.owners || []).map(o => (o.id === ownerId ? { ...o, [field]: value } : o));
    handleChange('owners', owners);
  };

  const handleAddOwner = () => {
    const newOwner = {
      id: `owner-${Date.now()}`,
      name: '',
      contactNumber: '',
      email: '',
      qrCodes: [],
    };
    handleChange('owners', [...(formData.owners || []), newOwner]);
  };

  const handleRemoveOwner = (ownerId) => {
    // Courts pointing at this owner would silently become unbookable, so
    // say so up front and clear the assignment as part of the same edit
    // rather than leaving dangling ownerIds behind.
    const assigned = (formData.courts || []).filter(c => String(c.ownerId) === String(ownerId));
    const owner = (formData.owners || []).find(o => o.id === ownerId);
    showModal({
      title: 'Remove Owner',
      message: assigned.length > 0
        ? `${(owner?.name || '').trim() || 'This owner'} is assigned to ${assigned.length} court`
          + `${assigned.length > 1 ? 's' : ''} (${assigned.map(c => (c.name || '').trim() || `Court ${c.number}`).join(', ')}).\n\n`
          + `Removing the owner leaves ${assigned.length > 1 ? 'those courts' : 'that court'} unassigned, and guests `
          + `will not be able to book ${assigned.length > 1 ? 'them' : 'it'} until you pick a new owner.`
        : 'Remove this owner and their QR codes?',
      type: 'warning',
      confirmText: 'Remove',
      showCancel: true,
      cancelText: 'Cancel',
      onConfirm: () => {
        handleChange('owners', (formData.owners || []).filter(o => o.id !== ownerId));
        if (assigned.length > 0) {
          handleChange('courts', (formData.courts || []).map(
            c => (String(c.ownerId) === String(ownerId) ? { ...c, ownerId: null } : c)
          ));
        }
        closeModal();
      },
    });
  };

  const handleOwnerQRFileChange = async (ownerId, e) => {
    const file = e.target.files[0];
    // Cleared immediately so re-picking the same file still fires onChange.
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showModal({ title: 'Invalid File', message: 'Please select an image file.', type: 'warning', confirmText: 'OK' });
      return;
    }
    setUploadingOwnerQrId(ownerId);
    try {
      // Same preset as the club-wide QR: the modules have to stay sharp
      // enough for a phone camera to resolve them after compression.
      const { dataUrl } = await compressImage(file, IMAGE_PRESETS.QR);
      const owners = (formData.owners || []).map(o => (
        o.id === ownerId
          ? { ...o, qrCodes: [...(o.qrCodes || []), { id: `oqr-${Date.now()}`, name: '', url: dataUrl, accName: '', accNumber: '' }] }
          : o
      ));
      handleChange('owners', owners);
    } catch (error) {
      showModal({ title: 'Error', message: 'Failed to upload QR code: ' + error.message, type: 'error', confirmText: 'OK' });
    } finally {
      setUploadingOwnerQrId(null);
    }
  };

  const updateOwnerQr = (ownerId, qrId, field, value) => {
    const owners = (formData.owners || []).map(o => (
      o.id === ownerId
        ? { ...o, qrCodes: (o.qrCodes || []).map(q => (q.id === qrId ? { ...q, [field]: value } : q)) }
        : o
    ));
    handleChange('owners', owners);
  };

  const handleRemoveOwnerQr = (ownerId, qrId) => {
    const owners = (formData.owners || []).map(o => (
      o.id === ownerId ? { ...o, qrCodes: (o.qrCodes || []).filter(q => q.id !== qrId) } : o
    ));
    handleChange('owners', owners);
  };

  const handleQRFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showModal({
          title: 'Invalid File',
          message: 'Please select an image file.',
          type: 'warning',
          confirmText: 'OK'
        });
        return;
      }
      setQrImageFile(file);
    }
  };

  if (configLoading) {
    return (
      <div className="admin-config" style={{ color: COLORS.textSecondary, textAlign: 'center', padding: '2rem' }}>
        Loading configuration...
      </div>
    );
  }

  const tabs = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'courts', label: '🏆 Sports & Courts' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'booking', label: 'Booking Settings' },
    { id: 'equipment', label: 'Equipment' },
    { id: 'details', label: 'Court Details' },
    { id: 'policies', label: 'Policies' },
    { id: COURT_REMINDERS_TAB_ID, label: COURT_REMINDERS_TAB_LABEL },
    { id: 'photos', label: '📷 Photos' },
    { id: 'landing', label: '🏠 Landing Page' },
    { id: 'payment', label: '💳 Payment' },
    { id: 'theme', label: '🎨 Theme' },
    { id: 'system', label: '⚙️ System' },
  ];

  return (
    <div className="admin-config" style={{ color: COLORS.textSecondary }}>
      <h2 className="section-title">⚙️ Configuration</h2>
      <p style={{ marginBottom: '1.5rem', opacity: 0.7, fontSize: '0.9rem' }}>
        Manage your application settings. Changes are saved to the database.
      </p>

      {/* Saved is not the same as live: the landing page is built from a
          snapshot of this config (scripts/snapshotConfig.js), so a change
          reaches guests only when the site is rebuilt. Fed the SAVED config
          rather than formData — it reads `lastModified`, which only exists on
          what was actually written. Renders nothing when there is nothing
          pending. */}
      <PendingChangesBanner config={config} />
      {/* Tabs */}
      <div className="config-tabs" style={{ display: 'flex', gap: '4px', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '6px 12px',
              background: activeTab === tab.id ? COLORS.neonLime : COLORS.cardBg,
              color: activeTab === tab.id ? COLORS.deepBg : COLORS.textSecondary,
              border: activeTab === tab.id ? `1px solid ${COLORS.neonLime}` : `1px solid ${COLORS.line}`,
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '0.75rem',
              transition: 'all 0.2s ease',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div
        className="config-content"
        style={{
          background: COLORS.cardSolid,
          borderRadius: '8px',
          padding: '1rem',
          border: `1px solid ${COLORS.line}`,
        }}
      >
        {/* Basic Info Tab */}
        {activeTab === 'basic' && (
          <div className="config-section">
            <h3 style={{ color: COLORS.ink, marginBottom: '0.75rem', fontSize: '0.9rem' }}>Basic Information</h3>
            <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
              <div className="form-group">
                <label>Court Name</label>
                <input
                  type="text"
                  value={formData.courtName || ''}
                  onChange={(e) => handleChange('courtName', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Contact Number</label>
                <input
                  type="tel"
                  value={formData.contactNumber || ''}
                  onChange={(e) => handleChange('contactNumber', e.target.value)}
                  placeholder="09123456789"
                />
              </div>
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={formData.emailAddress || ''}
                  onChange={(e) => handleChange('emailAddress', e.target.value)}
                  placeholder="info@paddlegrip.com"
                />
              </div>
              <div className="form-group">
                <label>Facebook Page Name</label>
                <input
                  type="text"
                  value={formData.facebookPage || ''}
                  onChange={(e) => handleChange('facebookPage', e.target.value)}
                  placeholder="@PaddleGrip"
                />
              </div>
              <div className="form-group">
                <label>Facebook Page Url</label>
                <input
                  type="text"
                  value={formData.facebookPageUrl || ''}
                  onChange={(e) => handleChange('facebookPageUrl', e.target.value)}
                  placeholder="https://www.facebook.com/YOUR_PAGE_NAME"
                />
              </div>
              <div className="form-group">
                <label>Location Text</label>
                <input
                  type="text"
                  value={formData.locationText || ''}
                  onChange={(e) => handleChange('locationText', e.target.value)}
                  placeholder="Purok Mahogany, Medellin, Cebu"
                />
              </div>
              <div className="form-group">
                <label>Landmark</label>
                <input
                  type="text"
                  value={formData.landmark || ''}
                  onChange={(e) => handleChange('landmark', e.target.value)}
                  placeholder="Near barangay hall"
                />
              </div>
              <div className="form-group">
                <label>Google Maps URL</label>
                <input
                  type="url"
                  value={formData.mapsUrl || ''}
                  onChange={(e) => handleChange('mapsUrl', e.target.value)}
                  placeholder="https://www.google.com/maps/place/..."
                />
              </div>
              <div className="form-group">
                <label>Admin Email (for reports)</label>
                <input
                  type="email"
                  value={formData.adminEmail || ''}
                  onChange={(e) => handleChange('adminEmail', e.target.value)}
                  placeholder="admin@paddlegrip.com"
                />
              </div>
            </div>
          </div>
        )}

        {/* Sports & Courts Tab */}
        {activeTab === 'courts' && (
          <div className="config-section">
            <h3 style={{ color: COLORS.ink, marginBottom: '0.35rem', fontSize: '0.9rem' }}>Sports</h3>
            <p style={{ opacity: 0.65, fontSize: '0.78rem', margin: '0 0 0.75rem' }}>
              Add a sport for each type of court this club offers (e.g. Pickleball, Tennis). Guests choose a sport
              before booking once there's more than one. Each sport has its own hourly rates.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '0.5rem' }}>
              {(formData.sports || []).map(sport => (
                <div
                  key={sport.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap',
                    background: COLORS.cardBg, padding: '0.6rem 0.75rem', borderRadius: '8px',
                    border: `1px solid ${COLORS.line}`,
                  }}
                >
                  <input
                    type="text"
                    value={sport.icon || ''}
                    onChange={(e) => updateSport(sport.id, 'icon', e.target.value)}
                    placeholder="🏓"
                    maxLength={4}
                    style={{ width: '42px', textAlign: 'center' }}
                    aria-label="Sport icon"
                  />
                  <input
                    type="text"
                    value={sport.name || ''}
                    onChange={(e) => updateSport(sport.id, 'name', e.target.value)}
                    placeholder="Sport name"
                    style={{ width: '140px' }}
                    aria-label="Sport name"
                  />
                  <button
                    onClick={() => handleRemoveSport(sport.id)}
                    disabled={(formData.sports || []).length <= 1}
                    style={{
                      marginLeft: 'auto', background: 'none', border: 'none',
                      color: (formData.sports || []).length <= 1 ? COLORS.inkFaint : '#c0392b',
                      cursor: (formData.sports || []).length <= 1 ? 'not-allowed' : 'pointer', fontSize: '0.85rem',
                    }}
                    aria-label="Remove sport"
                  >
                    ✕
                  </button>

                  {/* Hourly rate bands. Replaces the old off-peak/peak pair —
                      add as many as the club actually charges. A band whose
                      "to" hour is at or before its "from" hour wraps past
                      midnight, so 5PM -> 6AM is one continuous evening block. */}
                  <div style={{ flexBasis: '100%', marginTop: '0.6rem' }}>
                    <label style={{ fontSize: '0.7rem', display: 'block', marginBottom: '0.35rem' }}>
                      Hourly rates
                    </label>
                    {/* Whether the guest and admin grids show a separator
                        where each rate starts, and its design — set here,
                        next to each rate's own separator preview. */}
                    <RateSeparatorDisplaySettings
                      show={isRateSeparatorShown(formData)}
                      design={resolveRateSeparatorDesign(formData)}
                      onShowChange={(next) => handleChange(RATE_SEPARATOR_SHOW_CONFIG_KEY, next)}
                      onDesignChange={(next) => handleChange(RATE_SEPARATOR_DESIGN_CONFIG_KEY, next)}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                      {(sport.rateBands || []).map((band) => (
                        <div
                          key={band.id}
                          style={{
                            display: 'flex', alignItems: 'flex-end', gap: '0.5rem', flexWrap: 'wrap',
                            padding: '0.45rem 0.6rem', borderRadius: '6px',
                            border: `1px solid ${COLORS.line}`, background: COLORS.surface,
                          }}
                        >
                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '0.66rem' }}>Name</label>
                            <input
                              type="text" value={band.name || ''}
                              onChange={(e) => updateBand(sport.id, band.id, 'name', e.target.value)}
                              placeholder="e.g. Off-peak"
                              style={{ width: '110px' }}
                            />
                          </div>
                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '0.66rem' }}>From (24h)</label>
                            <input
                              type="number" min="0" max="23" value={band.startHour ?? ''}
                              onChange={(e) => updateBand(sport.id, band.id, 'startHour', parseInt(e.target.value) || 0)}
                              style={{ width: '70px' }}
                            />
                          </div>
                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '0.66rem' }}>To (24h)</label>
                            <input
                              type="number" min="0" max="23" value={band.endHour ?? ''}
                              onChange={(e) => updateBand(sport.id, band.id, 'endHour', parseInt(e.target.value) || 0)}
                              style={{ width: '70px' }}
                            />
                          </div>
                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '0.66rem' }}>{getCurrencySymbol(formData)}/hour</label>
                            <input
                              type="number" min="0" value={band.price ?? ''}
                              step="0.01"
                              onChange={(e) => updateBand(sport.id, band.id, 'price', parsePriceInput(e.target.value))}
                              style={{ width: '90px' }}
                            />
                          </div>
                          <span style={{ fontSize: '0.7rem', opacity: 0.7, paddingBottom: '0.35rem' }}>
                            {formatHourLabel(Number(band.startHour) || 0)} – {formatHourLabel(Number(band.endHour) || 0)}
                            {Number(band.endHour) <= Number(band.startHour) ? ' (next day)' : ''}
                          </span>
                          <button
                            onClick={() => handleRemoveBand(sport.id, band.id)}
                            style={{
                              marginLeft: 'auto', background: 'none', border: 'none',
                              color: '#c0392b', cursor: 'pointer', fontSize: '0.8rem', paddingBottom: '0.35rem',
                            }}
                            aria-label="Remove rate band"
                          >
                            ✕
                          </button>

                          {/* Which weekdays this rate applies to. All seven on
                              (the default) is stored as an empty list, so a
                              club with one price all week keeps the simpler
                              config it already had. */}
                          <div style={{
                            flexBasis: '100%', display: 'flex', alignItems: 'center',
                            gap: '0.3rem', flexWrap: 'wrap', marginTop: '0.15rem',
                          }}>
                            <span style={{ fontSize: '0.66rem', color: COLORS.textSecondary, marginRight: '0.15rem' }}>
                              Applies on
                            </span>
                            {WEEKDAYS.map((day) => {
                              const on = isBandDayOn(band, day.index);
                              return (
                                <button
                                  key={day.index}
                                  type="button"
                                  onClick={() => toggleBandDay(sport.id, band.id, day.index)}
                                  aria-pressed={on}
                                  title={day.label}
                                  style={{
                                    padding: '0.15rem 0.4rem', borderRadius: '999px', cursor: 'pointer',
                                    fontSize: '0.66rem', lineHeight: 1.5,
                                    border: `1px solid ${on ? COLORS.cyanDeep : COLORS.line}`,
                                    background: on ? COLORS.cyanDeep : 'transparent',
                                    color: on ? '#ffffff' : COLORS.textSecondary,
                                  }}
                                >
                                  {day.short}
                                </button>
                              );
                            })}
                            <span style={{ fontSize: '0.66rem', opacity: 0.7, marginLeft: '0.2rem' }}>
                              {describeBandDays(band)}
                            </span>
                          </div>

                          {/* How this rate's separator looks in the guest
                              time slot grid — icon, colour, live preview. */}
                          <RateBandSeparatorSettings
                            band={band}
                            design={resolveRateSeparatorDesign(formData)}
                            price={`${formatMoney(Number(band.price) || 0, formData)}/hr`}
                            hours={`${formatHourLabel(Number(band.startHour) || 0)} \u2013 ${formatHourLabel(Number(band.endHour) || 0)}`}
                            onChange={(field, value) => updateBand(sport.id, band.id, field, value)}
                          />
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => handleAddBand(sport.id)}
                      style={{
                        marginTop: '0.4rem', padding: '0.35rem 0.7rem', background: 'none',
                        border: `1px dashed ${COLORS.line}`, borderRadius: '6px', cursor: 'pointer',
                        fontSize: '0.74rem', color: COLORS.textSecondary,
                      }}
                    >
                      + Add rate
                    </button>
                    {/* Schedule conflicts. Errors mean a band is silently dead
                        or would mis-charge; warnings are worth a look but
                        don't stop a save. */}
                    {bandIssues(sport).map((issue, idx) => (
                      <p
                        key={`${issue.code}-${idx}`}
                        style={{
                          margin: '0.4rem 0 0', fontSize: '0.72rem', lineHeight: 1.45,
                          color: issue.level === ISSUE_LEVELS.ERROR ? '#c0392b' : COLORS.warning,
                          fontWeight: issue.level === ISSUE_LEVELS.ERROR ? 600 : 400,
                        }}
                      >
                        {issue.level === ISSUE_LEVELS.ERROR ? '⚠ ' : ''}{issue.message}
                      </p>
                    ))}
                  </div>

                  {/* Open Play — pay-per-player rotating queue sessions, separate
                      from reserving a whole court (see OpenPlayModal.jsx). Off by
                      default; enabling shows a "Join Open Play" option for this
                      sport on the guest-facing side. */}
                  <div style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap',
                    borderTop: `1px dashed ${COLORS.line}`, paddingTop: '0.5rem', marginTop: '0.15rem',
                  }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.78rem', color: COLORS.textSecondary }}>
                      <input
                        type="checkbox"
                        checked={!!sport.openPlayEnabled}
                        onChange={(e) => updateSport(sport.id, 'openPlayEnabled', e.target.checked)}
                      />
                      Open Play
                    </label>
                    {sport.openPlayEnabled && (
                      <>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label style={{ fontSize: '0.7rem' }}>{getCurrencySymbol(formData)} / player</label>
                          <input
                            type="number" min="0" value={sport.openPlayPricePerPlayer ?? ''}
                            step="0.01"
                            onChange={(e) => updateSport(sport.id, 'openPlayPricePerPlayer', parsePriceInput(e.target.value))}
                            style={{ width: '90px' }}
                          />
                        </div>
                        <div className="form-group" style={{ margin: 0, flex: '1 1 200px' }}>
                          <label style={{ fontSize: '0.7rem' }}>Schedule note (shown to guests)</label>
                          <input
                            type="text" value={sport.openPlayNote || ''}
                            onChange={(e) => updateSport(sport.id, 'openPlayNote', e.target.value)}
                            placeholder="e.g. Every Sat & Sun, 4-6PM"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={handleAddSport}
              style={{
                padding: '0.45rem 0.8rem', background: 'none', border: `1px dashed ${COLORS.line}`,
                borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem', color: COLORS.textSecondary,
              }}
            >
              + Add sport
            </button>

            <h3 style={{ color: COLORS.ink, margin: '1.25rem 0 0.35rem', fontSize: '0.9rem' }}>Courts</h3>
            <p style={{ opacity: 0.65, fontSize: '0.78rem', margin: '0 0 0.75rem' }}>
              Add one entry per physical court and assign it to a sport. Court name is what guests see on the
              booking grid — leave blank to just show &quot;Court&quot; plus its number. Untick Available to
              temporarily close a court (it stays listed but nothing can be booked on it). The override boxes
              set this court's price for one rate band — leave any of them blank to use the sport's rate. Booked label controls whether a taken hour
              on the guest grid shows the booker's initials.
            </p>

            <OwnershipWarningBanner config={formData} context="config" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.5rem' }}>
              {(formData.courts || []).slice().sort((a, b) => Number(a.number) - Number(b.number)).map(court => (
                <div
                  key={court.id}
                  style={{
                    display: 'flex', flexDirection: 'column', gap: '0.55rem',
                    background: COLORS.cardBg, padding: '0.6rem 0.75rem', borderRadius: '8px',
                    border: `1px solid ${COLORS.line}`,
                  }}
                >
                  {/* Row 1 — what the court IS. */}
                  <div style={COURT_CARD_ROW}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.7rem' }}>Court #</label>
                      <input
                        type="number" min="1" value={court.number}
                        onChange={(e) => updateCourt(court.id, 'number', parseInt(e.target.value) || 1)}
                        style={{ width: '60px' }}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.7rem' }}>Court name</label>
                      <input
                        type="text"
                        placeholder={`Court ${court.number}`}
                        value={court.name || ''}
                        onChange={(e) => updateCourt(court.id, 'name', e.target.value)}
                        style={{ minWidth: '140px' }}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.7rem' }}>Sport</label>
                      <select
                        value={court.sportId}
                        onChange={(e) => updateCourt(court.id, 'sportId', e.target.value)}
                        style={{ minWidth: '120px' }}
                      >
                        {(formData.sports || []).map(s => (
                          <option key={s.id} value={s.id}>{s.icon} {s.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.7rem' }}>Court type</label>
                      <select
                        value={court.isAddOn === true ? 'addon' : court.indoor === true ? 'indoor' : court.indoor === false ? 'outdoor' : ''}
                        onChange={(e) => updateCourtType(court.id, e.target.value)}
                        style={{ minWidth: '110px' }}
                      >
                        <option value="">Not set</option>
                        <option value="indoor">Indoor</option>
                        <option value="outdoor">Outdoor</option>
                        <option value="addon">Add-on</option>
                      </select>
                    </div>
                    {/* Which owner collects payment for this court. Only
                        shown while Shared Ownership is on (Config -> System);
                        the list comes from the owners set up under Payment.
                        Leaving it unassigned makes the court unbookable —
                        the banner above says so, and a guest tapping it gets
                        a "please contact us" message rather than a checkout
                        with nowhere to send the money. */}
                    {formData.sharedOwnershipEnabled === true && (
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontSize: '0.7rem' }}>Owner</label>
                        <select
                          value={court.ownerId || ''}
                          onChange={(e) => updateCourt(court.id, 'ownerId', e.target.value || null)}
                          style={{
                            minWidth: '150px',
                            // A missing owner is a live booking outage, not a
                            // cosmetic gap — make it findable in a long list.
                            borderColor: court.ownerId ? undefined : '#c0392b',
                          }}
                        >
                          <option value="">— Not assigned —</option>
                          {(formData.owners || []).map(o => (
                            <option key={o.id} value={o.id}>{(o.name || '').trim() || 'Unnamed owner'}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <button
                      onClick={() => handleRemoveCourt(court.id)}
                      style={{ marginLeft: 'auto', alignSelf: 'center', background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer', fontSize: '0.85rem' }}
                      aria-label="Remove court"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Row 1b — only for add-ons. An add-on is booked ALONGSIDE
                      a court, never on its own, so it needs to say which
                      courts count as "alongside" and what to tell a guest who
                      taps it on an hour they haven't booked a court for. */}
                  {court.isAddOn === true && (() => {
                    const pairable = pairableCourtsFor(court);
                    const paired = (Array.isArray(court.addOnPairedCourts) ? court.addOnPairedCourts : []).map(Number);
                    return (
                      <div style={{ borderTop: `1px dashed ${COLORS.line}`, paddingTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.7rem', marginBottom: '0.2rem' }}>Pairs with</label>
                          <p style={{ margin: '0 0 0.35rem', fontSize: '0.72rem', opacity: 0.65 }}>
                            Guests can only book an hour on this add-on when they have <strong>the same hour</strong> on
                            one of the courts ticked here. Tick none to allow any court in this sport.
                          </p>
                          {pairable.length === 0 ? (
                            <span style={{ fontSize: '0.72rem', color: '#c0392b' }}>
                              This sport has no ordinary courts yet — add one before pairing.
                            </span>
                          ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 0.9rem' }}>
                              {pairable.map(partner => (
                                <label
                                  key={partner.id}
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.78rem', cursor: 'pointer' }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={paired.includes(Number(partner.number))}
                                    onChange={() => toggleAddOnPairedCourt(court.id, partner.number)}
                                    style={{ cursor: 'pointer' }}
                                  />
                                  <span>{(partner.name || '').trim() || `Court ${partner.number}`}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label
                            style={{
                              fontSize: '0.7rem', display: 'flex', alignItems: 'center',
                              gap: '0.5rem', flexWrap: 'wrap',
                            }}
                          >
                            <span>Message when no court is selected</span>
                            {/* Anything saved here silently outranks the default
                                wording, so a club that typed a message once has
                                no way back to it short of guessing what it said.
                                This empties the field, which IS "use the
                                default" (see getAddOnRequiredMessage). */}
                            {(court.addOnRequiredMessage || '').trim() && (
                              <button
                                type="button"
                                onClick={() => updateCourt(court.id, 'addOnRequiredMessage', '')}
                                style={{
                                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                  fontSize: '0.7rem', color: COLORS.textSecondary, textDecoration: 'underline',
                                }}
                              >
                                Use default wording
                              </button>
                            )}
                          </label>
                          <textarea
                            rows={4}
                            value={court.addOnRequiredMessage || ''}
                            onChange={(e) => updateCourt(court.id, 'addOnRequiredMessage', e.target.value)}
                            placeholder={DEFAULT_ADDON_REQUIRED_MESSAGE}
                            style={{ width: '100%', maxWidth: '520px', resize: 'vertical' }}
                          />
                          <small style={{ display: 'block', marginTop: '0.25rem', opacity: 0.6, fontSize: '0.7rem' }}>
                            Shown to a guest who taps this add-on on an hour with no paired court selected. Leave blank
                            to use the default wording. Write <code>{'{item}'}</code> for this add-on&apos;s name and{' '}
                            <code>{'{courts}'}</code> for the courts ticked above — both fill themselves in, so renaming
                            a court or changing the pairing never leaves a stale name in the message. Blank lines are
                            kept, so a second paragraph (e.g. what&apos;s included with the rental) renders as one.
                          </small>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Row 2 — what it COSTS. One override box per rate band of
                      this court's sport; blank inherits that band's price, so
                      a court that only differs at peak needs just the one
                      number filled in. */}
                  {getSportRateBands(formData, court.sportId).length > 0 && (
                    <div style={{ ...COURT_CARD_ROW, borderTop: `1px dashed ${COLORS.line}`, paddingTop: '0.5rem' }}>
                      {getSportRateBands(formData, court.sportId).map((band) => (
                        <div className="form-group" key={band.id} style={{ margin: 0 }}>
                          <label style={{ fontSize: '0.7rem' }}>
                            {band.name || formatHourLabel(Number(band.startHour) || 0)} override ({getCurrencySymbol(formData)})
                          </label>
                          <input
                            type="number" min="0" placeholder="—"
                            value={court.bandRates?.[band.id] ?? ''}
                            onChange={(e) => updateCourtBandRate(
                              court.id, band.id, e.target.value === '' ? '' : parsePriceInput(e.target.value)
                            )}
                            style={{ width: '90px' }}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Row 3 — the on/off switches, kept together so they can't
                      wrap apart from each other. */}
                  <div style={{ ...COURT_CARD_ROW, alignItems: 'center', gap: '1.25rem', borderTop: `1px dashed ${COLORS.line}`, paddingTop: '0.5rem' }}>
                    {/* Untick to temporarily close this court — it stays
                        visible on the guest grid but every hour renders
                        greyed out as "Unavailable" (see isCourtAvailable). */}
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.7rem' }}>Available</label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.7rem', cursor: 'pointer', paddingTop: '0.15rem', whiteSpace: 'nowrap' }}>
                        <input
                          type="checkbox"
                          checked={court.available !== false}
                          onChange={(e) => updateCourt(court.id, 'available', e.target.checked)}
                        />
                        <span>{court.available !== false ? 'Open' : 'Closed'}</span>
                      </label>
                    </div>
                    {/* Whether a booked hour on the guest grid names who took it.
                        Off by default: the cell just reads "BOOKED". Ticked, it
                        appends the booker's initials, e.g. a booking under
                        "Bryan Namoc" renders as "BOOKED (BN)" — initials are
                        derived per booking, never a readable name (see
                        getDisplayName in TimeSlotSelection.jsx). */}
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.7rem' }}>Booked label</label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.7rem', cursor: 'pointer', paddingTop: '0.15rem', whiteSpace: 'nowrap' }}>
                        <input
                          type="checkbox"
                          checked={court.showBookedInitials === true}
                          onChange={(e) => updateCourt(court.id, 'showBookedInitials', e.target.checked)}
                        />
                        {/* A format preview, not a value — there's no booking in
                            scope here. The real cell substitutes the actual
                            guest's initials (getDisplayName in
                            TimeSlotSelection.jsx), so this says "initials"
                            rather than a made-up pair of letters that would
                            read as hardcoded. */}
                        <span>Display Initials</span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
              {(formData.courts || []).length === 0 && (
                <p style={{ opacity: 0.6, fontSize: '0.8rem' }}>No courts yet. Add one below.</p>
              )}
            </div>
            <button
              onClick={handleAddCourt}
              style={{
                padding: '0.45rem 0.8rem', background: 'none', border: `1px dashed ${COLORS.line}`,
                borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem', color: COLORS.textSecondary,
              }}
            >
              + Add court
            </button>

            <div className="form-group" style={{ marginTop: '1.25rem' }}>
              <label>Time Slots</label>
              <p style={{ opacity: 0.65, fontSize: '0.78rem', margin: '0.25rem 0 0.75rem' }}>
                The hours guests can book. Most clubs need only the default list; add a set
                when some days differ — a venue that closes at 10pm at weekends, or does not
                open at all on a particular day.
              </p>

              <TimeSlotSetEditor
                title="Default hours"
                subtitle="Used on every day no set below claims."
                slots={formData.timeSlots || []}
                onSlotsChange={(next) => handleChange('timeSlots', next)}
              />

              {(formData.timeSlotSets || []).map((set) => (
                <TimeSlotSetEditor
                  key={set.id}
                  label={set.label}
                  onLabelChange={(value) => handleUpdateSlotSet(set.id, { label: value })}
                  subtitle="Overrides the default hours on the days selected below."
                  days={Array.isArray(set.days) ? set.days : []}
                  onDaysChange={(next) => handleUpdateSlotSet(set.id, { days: next })}
                  courtOptions={slotSetCourtOptions}
                  courts={Array.isArray(set.courts) ? set.courts : []}
                  onCourtsChange={(next) => handleUpdateSlotSet(set.id, { courts: next })}
                  slots={set.slots || []}
                  onSlotsChange={(next) => handleUpdateSlotSet(set.id, { slots: next })}
                  onRemove={() => handleRemoveSlotSet(set.id)}
                />
              ))}

              {/* Only flagged when two sets claim the same day AND the same
                  court. Splitting a day across courts is the point of the
                  court boxes, so warning about that would be noise. */}
              {duplicateSlotSetDays.length > 0 && (
                <p style={{ color: COLORS.warning, fontSize: '0.75rem', margin: '0 0 0.6rem' }}>
                  {duplicateSlotSetDays.join(', ')} {duplicateSlotSetDays.length === 1 ? 'is' : 'are'} claimed by
                  more than one set on the same court. Those sets&rsquo; hours are combined
                  on {duplicateSlotSetDays.length === 1 ? 'that day' : 'those days'}.
                </p>
              )}

              <button
                type="button"
                onClick={handleAddSlotSet}
                style={{
                  padding: '0.45rem 0.8rem', background: 'none', color: COLORS.cyanDeep,
                  border: `1px dashed ${COLORS.cyanDeep}`, borderRadius: '4px',
                  cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem',
                }}
              >
                + Add hours for specific days
              </button>
            </div>
          </div>
        )}

        {/* Pricing Tab */}
        {activeTab === 'pricing' && (
          <div className="config-section">
            <h3 style={{ color: COLORS.ink, marginBottom: '0.35rem', fontSize: '0.9rem' }}>Pricing Configuration</h3>
            <p style={{ opacity: 0.65, fontSize: '0.78rem', margin: '0 0 0.75rem' }}>
              Hourly rate bands and per-court overrides live on the <strong>Sports & Courts</strong> tab — each
              sport defines its own bands (name, hours and price), and each court can override any of them.
              The fields below apply club-wide.
            </p>
            <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
              <div className="form-group">
                <label>Processing Fee ({getCurrencySymbol(formData)})</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.bookingFee}
                    onChange={(e) => handleChange('bookingFee', parsePriceInput(e.target.value))}
                    style={{ width: '90px' }}
                  />
                  {/* Unticked (the default) the fee is charged once for the
                      whole reservation, which is what it has always done.
                      Ticked, it multiplies by every court-hour — see
                      calculateBookingFee in bookingUtils.js. */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.75rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    <input
                      type="checkbox"
                      checked={formData.bookingFeePerHour === true}
                      onChange={(e) => handleChange('bookingFeePerHour', e.target.checked)}
                    />
                    <span>Charge per hour</span>
                  </label>
                </div>
                <small style={{ display: 'block', marginTop: '0.25rem', opacity: 0.6, fontSize: '0.7rem' }}>
                  Applied to confirmed bookings.{' '}
                  {formData.bookingFeePerHour === true
                    // A worked example, not a restatement of the label —
                    // "per hour" alone doesn't say whether a second court
                    // doubles it, and that's the part admins get wrong.
                    ? <>Multiplied by every court-hour booked: 2 courts × 3 hours ={' '}
                        <strong>{formatMoney((Number(formData.bookingFee) || 0) * 6, formData)}</strong>{' '}
                        at {formatMoney(formData.bookingFee, formData)}. Every date in a multi-date booking counts.</>
                    : <>Charged once per reservation, however many courts, hours or dates it covers.</>}
                </small>
              </div>
              <div className="form-group">
                <label>Payment Timeout (minutes)</label>
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={formData.paymentTimeoutMinutes ?? 5}
                  onChange={(e) => handleChange('paymentTimeoutMinutes', parseInt(e.target.value) || 1)}
                  style={{ width: '90px' }}
                />
                <small style={{ display: 'block', marginTop: '0.25rem', opacity: 0.6, fontSize: '0.7rem' }}>
                  Guests must upload a payment receipt within this many minutes of reaching checkout, or the slot is released.
                </small>
              </div>
              <div className="form-group">
                {/* A MINIMUM, not a fixed downpayment: at 50 the admin may
                    accept anything from half the total upwards. 0 (or blank)
                    switches the whole feature off — see
                    getPartialPaymentPercent in partialPaymentUtil.js. */}
                <label>Partial Payment Allowed (%)</label>
                <input
                  type="number"
                  min="0"
                  max={MAX_PARTIAL_PAYMENT_PERCENT}
                  step="1"
                  value={formData.partialPaymentPercent ?? DEFAULT_PARTIAL_PAYMENT_PERCENT}
                  onChange={(e) => handleChange(
                    'partialPaymentPercent',
                    // Clamped on the way in as well as on the way out, so a
                    // typo can't save a rule that lets a booking through for
                    // less than the club meant to accept.
                    Math.min(MAX_PARTIAL_PAYMENT_PERCENT, Math.max(0, parseInt(e.target.value, 10) || 0)),
                  )}
                  style={{ width: '90px' }}
                />
                <small style={{ display: 'block', marginTop: '0.25rem', opacity: 0.6, fontSize: '0.7rem' }}>
                  {getPartialPaymentPercent(formData) > 0
                    ? <>The least a guest may pay up front for staff to confirm a reservation with{' '}
                      <strong>Confirm (Partial)</strong>. A {formatMoney(1000, formData)} booking would need at least{' '}
                      <strong>{formatMoney(getMinimumPartialAmount(1000, getPartialPaymentPercent(formData)), formData)}</strong>,
                      and the rest is tracked as a balance until someone presses <strong>Mark Fully Paid</strong>.</>
                    : <>Set to <strong>0</strong> — full payment is required and the{' '}
                      <strong>Confirm (Partial)</strong> button stays hidden. Enter a percentage (e.g. 50) to let
                      staff confirm a reservation against a downpayment.</>}
                </small>
              </div>
              <div className="form-group">
                <label>Open Past Midnight Until</label>
                <input
                  type="number"
                  min="0"
                  max={MAX_NEXT_DAY_GRID_END_HOUR}
                  value={formData.nextDayGridEndHour ?? DEFAULT_NEXT_DAY_GRID_END_HOUR}
                  onChange={(e) => handleChange(
                    'nextDayGridEndHour',
                    // Clamped on the way in as well as on the way out
                    // (getNextDayGridEndHour): a saved out-of-range value would
                    // otherwise sit in the config looking valid.
                    Math.min(MAX_NEXT_DAY_GRID_END_HOUR, Math.max(0, parseInt(e.target.value, 10) || 0)),
                  )}
                  style={{ width: '90px' }}
                />
                <small style={{ display: 'block', marginTop: '0.25rem', opacity: 0.6, fontSize: '0.7rem' }}>
                  For clubs that trade past midnight. The booking grids continue under the day's own hours
                  into the next morning up to this hour, each row labelled with the date it belongs to.
                  Only hours you have actually added in <strong>Time Slots</strong> appear, and only while
                  they run on unbroken from the night before &mdash; set it to <strong>0</strong> to end every
                  grid at midnight.
                </small>
              </div>
            </div>
          </div>
        )}

        {/* Equipment Tab */}
        {activeTab === 'equipment' && (
          <div className="config-section">
            <h3 style={{ color: COLORS.ink, marginBottom: '0.35rem', fontSize: '0.9rem' }}>Equipment Rental</h3>
            <p style={{ margin: '0 0 0.9rem', fontSize: '0.78rem', color: COLORS.textSecondary }}>
              Add one entry per rentable item and assign it to a sport (e.g. paddles for Pickleball, rackets for
              Tennis) — guests only see the items that match the sport they're booking. Price and total owned
              determine availability; leave total owned as 0 if you don't offer that item.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem', marginBottom: '0.5rem' }}>
              {(formData.equipment || []).map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap',
                    padding: '0.75rem', border: `1px solid ${COLORS.line}`, borderRadius: '10px',
                    background: COLORS.cardBg,
                  }}
                >
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '0.7rem' }}>Item name</label>
                    <input
                      type="text"
                      value={item.label}
                      onChange={(e) => updateEquipmentItem(item.id, 'label', e.target.value)}
                      placeholder="e.g. Paddle"
                      style={{ width: '140px' }}
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '0.7rem' }}>Sport</label>
                    <select
                      value={item.sportId}
                      onChange={(e) => updateEquipmentItem(item.id, 'sportId', e.target.value)}
                      style={{ minWidth: '120px' }}
                    >
                      {(formData.sports || []).map(s => (
                        <option key={s.id} value={s.id}>{s.icon} {s.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '0.7rem' }}>Unit</label>
                    <input
                      type="text"
                      value={item.unit}
                      onChange={(e) => updateEquipmentItem(item.id, 'unit', e.target.value)}
                      placeholder="e.g. paddle, set, hr"
                      style={{ width: '90px' }}
                    />
                  </div>
                  {/* Session = one charge for the whole reservation, however
                      long it runs. Hourly = charged per booked hour, and the
                      guest picks which of their hours to keep it for (see
                      EquipmentRentalModal). Existing items have no value here
                      and read as "session", which is what they already did. */}
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '0.7rem' }}>Rental type</label>
                    <select
                      value={item.rentalType === 'hourly' ? 'hourly' : 'session'}
                      onChange={(e) => updateEquipmentItem(item.id, 'rentalType', e.target.value)}
                      style={{ minWidth: '110px' }}
                    >
                      <option value="session">Per session</option>
                      <option value="hourly">Per hour</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '0.7rem' }}>
                      Price ({getCurrencySymbol(formData)} / {item.rentalType === 'hourly' ? 'unit / hr' : 'unit'})
                    </label>
                    <input
                      type="number" min="0" value={item.price ?? 0}
                      step="0.01"
                      onChange={(e) => updateEquipmentItem(item.id, 'price', parsePriceInput(e.target.value))}
                      style={{ width: '90px' }}
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '0.7rem' }}>Total owned</label>
                    <input
                      type="number" min="0" value={item.inventoryTotal ?? 0}
                      onChange={(e) => updateEquipmentItem(item.id, 'inventoryTotal', parseInt(e.target.value) || 0)}
                      style={{ width: '90px' }}
                    />
                  </div>
                  <button
                    onClick={() => handleRemoveEquipmentItem(item.id)}
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer', fontSize: '0.85rem' }}
                    aria-label="Remove equipment item"
                  >
                    ✕
                  </button>
                </div>
              ))}
              {(formData.equipment || []).length === 0 && (
                <p style={{ opacity: 0.6, fontSize: '0.8rem' }}>No equipment items yet. Add one below.</p>
              )}
            </div>
            <button
              onClick={handleAddEquipmentItem}
              style={{
                padding: '0.45rem 0.8rem', background: 'none', border: `1px dashed ${COLORS.line}`,
                borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem', color: COLORS.textSecondary,
              }}
            >
              + Add equipment item
            </button>
          </div>
        )}

        {/* Court Details Tab */}
        {activeTab === 'details' && (
          <div className="config-section">
            <h3 style={{ color: COLORS.ink, marginBottom: '0.35rem', fontSize: '0.9rem' }}>Court Details</h3>
            <p style={{ opacity: 0.75, fontSize: '0.8rem', marginBottom: '0.75rem' }}>
              Add one card per thing worth telling guests — surface, lighting, hours, amenities, parking, anything.
              Each card shows on the landing page and in the booking screen&apos;s Court Information section.
              Drag-free ordering: cards appear in the order listed here.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '0.75rem' }}>
              {(formData.courtDetails || []).map((detail, index) => (
                <div
                  key={detail.id}
                  style={{
                    background: COLORS.cardBg, padding: '0.75rem', borderRadius: '10px',
                    border: `1px solid ${COLORS.line}`, display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.7rem' }}>Title</label>
                      <input
                        type="text"
                        value={detail.title || ''}
                        onChange={(e) => updateCourtDetail(detail.id, 'title', e.target.value)}
                        placeholder="e.g., Court Surface"
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.7rem' }}>Description</label>
                      <textarea
                        rows={2}
                        value={detail.description || ''}
                        onChange={(e) => updateCourtDetail(detail.id, 'description', e.target.value)}
                        placeholder="e.g., Specialized acrylic coatings over concrete base"
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: '0 0 auto' }}>
                    <button
                      onClick={() => moveCourtDetail(detail.id, -1)}
                      disabled={index === 0}
                      aria-label="Move up"
                      title="Move up"
                      style={{
                        background: 'none', border: 'none', cursor: index === 0 ? 'not-allowed' : 'pointer',
                        opacity: index === 0 ? 0.3 : 0.7, fontSize: '0.9rem', padding: '0.1rem 0.3rem',
                      }}
                    >▲</button>
                    <button
                      onClick={() => moveCourtDetail(detail.id, 1)}
                      disabled={index === (formData.courtDetails || []).length - 1}
                      aria-label="Move down"
                      title="Move down"
                      style={{
                        background: 'none', border: 'none',
                        cursor: index === (formData.courtDetails || []).length - 1 ? 'not-allowed' : 'pointer',
                        opacity: index === (formData.courtDetails || []).length - 1 ? 0.3 : 0.7,
                        fontSize: '0.9rem', padding: '0.1rem 0.3rem',
                      }}
                    >▼</button>
                    <button
                      onClick={() => handleRemoveCourtDetail(detail.id)}
                      aria-label="Remove detail"
                      title="Remove"
                      style={{
                        background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer',
                        fontSize: '0.85rem', padding: '0.1rem 0.3rem',
                      }}
                    >✕</button>
                  </div>
                </div>
              ))}
              {(formData.courtDetails || []).length === 0 && (
                <p style={{ opacity: 0.6, fontSize: '0.8rem' }}>
                  No details yet. Add one below — guests just won&apos;t see a Court Information section until you do.
                </p>
              )}
            </div>

            <button
              onClick={handleAddCourtDetail}
              style={{
                background: 'none', border: `1px dashed ${COLORS.line}`, color: COLORS.ink,
                padding: '0.5rem 0.9rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem',
              }}
            >
              + Add detail
            </button>
          </div>
        )}

        {/* Policies Tab */}
        {activeTab === 'policies' && (
          <div className="config-section">
            <h3 style={{ color: COLORS.ink, marginBottom: '0.35rem', fontSize: '0.9rem' }}>Court Rules &amp; Policies</h3>
            <p style={{ opacity: 0.75, fontSize: '0.8rem', marginBottom: '0.75rem' }}>
              Add one card per rule you want guests to know — food, pets, attire, smoking, cancellations,
              anything else. Cards show on the landing page and in the booking screen&apos;s rules section, in
              the order listed here. Remove any that don&apos;t apply to your club.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '0.75rem' }}>
              {(formData.policies || []).map((policy, index) => (
                <div
                  key={policy.id}
                  style={{
                    background: COLORS.cardBg, padding: '0.75rem', borderRadius: '10px',
                    border: `1px solid ${COLORS.line}`, display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.7rem' }}>Title</label>
                      <input
                        type="text"
                        value={policy.title || ''}
                        onChange={(e) => updatePolicy(policy.id, 'title', e.target.value)}
                        placeholder="e.g., Cancellation Policy"
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.7rem' }}>Description</label>
                      <textarea
                        rows={3}
                        value={policy.description || ''}
                        onChange={(e) => updatePolicy(policy.id, 'description', e.target.value)}
                        placeholder="e.g., Cancellations are non-refundable and subject to rescheduling only."
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: '0 0 auto' }}>
                    <button
                      onClick={() => movePolicy(policy.id, -1)}
                      disabled={index === 0}
                      aria-label="Move up"
                      title="Move up"
                      style={{
                        background: 'none', border: 'none', cursor: index === 0 ? 'not-allowed' : 'pointer',
                        opacity: index === 0 ? 0.3 : 0.7, fontSize: '0.9rem', padding: '0.1rem 0.3rem',
                      }}
                    >▲</button>
                    <button
                      onClick={() => movePolicy(policy.id, 1)}
                      disabled={index === (formData.policies || []).length - 1}
                      aria-label="Move down"
                      title="Move down"
                      style={{
                        background: 'none', border: 'none',
                        cursor: index === (formData.policies || []).length - 1 ? 'not-allowed' : 'pointer',
                        opacity: index === (formData.policies || []).length - 1 ? 0.3 : 0.7,
                        fontSize: '0.9rem', padding: '0.1rem 0.3rem',
                      }}
                    >▼</button>
                    <button
                      onClick={() => handleRemovePolicy(policy.id)}
                      aria-label="Remove policy"
                      title="Remove"
                      style={{
                        background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer',
                        fontSize: '0.85rem', padding: '0.1rem 0.3rem',
                      }}
                    >✕</button>
                  </div>
                </div>
              ))}
              {(formData.policies || []).length === 0 && (
                <p style={{ opacity: 0.6, fontSize: '0.8rem' }}>
                  No policies yet. Add one below — guests just won&apos;t see a rules section until you do.
                </p>
              )}
            </div>

            <button
              onClick={handleAddPolicy}
              style={{
                background: 'none', border: `1px dashed ${COLORS.line}`, color: COLORS.ink,
                padding: '0.5rem 0.9rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem',
              }}
            >
              + Add policy
            </button>
          </div>
        )}

        {/* Court Reminders Tab — the popup between the guest details and
            checkout (CourtRulesSheet) and the text it shows. */}
        {activeTab === COURT_REMINDERS_TAB_ID && (
          <div className="config-section">
            <h3 style={{ color: COLORS.ink, marginBottom: '0.35rem', fontSize: '0.9rem' }}>Court Reminders</h3>
            <p style={{ opacity: 0.75, fontSize: '0.8rem', marginBottom: '1rem' }}>
              What guests are reminded of right before they pay. They must scroll to the end of it and tap
              &quot;I Understand&quot; before checkout opens.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <label style={SYSTEM_TOGGLE_CARD}>
                <input
                  type="checkbox"
                  checked={formData[SHOW_COURT_RULES_CONFIG_KEY] === true}
                  onChange={(e) => handleChange(SHOW_COURT_RULES_CONFIG_KEY, e.target.checked)}
                  style={SYSTEM_TOGGLE_BOX}
                />
                <span>
                  <span style={{ display: 'block', fontWeight: 600, color: COLORS.ink, fontSize: '0.88rem' }}>
                    Show court rules before checkout
                  </span>
                  <span style={{ display: 'block', fontSize: '0.78rem', opacity: 0.7, marginTop: '2px' }}>
                    After filling in their details, guests are shown the reminders below and must scroll through
                    them and tap &quot;I Understand&quot; before checkout opens. Skipped when the reminders are
                    empty.
                  </span>
                </span>
              </label>

              <div className="form-group" style={{ margin: 0, maxWidth: '720px' }}>
                <label htmlFor="court-reminders-text">Court reminders</label>
                <p style={{ margin: '0 0 0.4rem', fontSize: '0.75rem', opacity: 0.7 }}>
                  Shown exactly as typed: line breaks and blank lines are kept. Start a line with &quot;-&quot;,
                  &quot;*&quot; or &quot;•&quot; for a bullet or &quot;1.&quot; for a numbered item, and indent it
                  with spaces to nest it under the one above.
                </p>
                <textarea
                  id="court-reminders-text"
                  rows={14}
                  maxLength={COURT_REMINDERS_TEXT_MAX_LENGTH}
                  value={formData[COURT_REMINDERS_TEXT_CONFIG_KEY] || ''}
                  onChange={(e) => handleChange(COURT_REMINDERS_TEXT_CONFIG_KEY, e.target.value)}
                  placeholder={'e.g.\nFood & Drinks\n- Allowed, but please Clean As You Go (CLAYGO).\n\nAttire\n- Non-marking court shoes only.\n\n1. Arrive 10 minutes before your slot.\n2. Return paddles to the rack.'}
                  style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.5 }}
                />
                <span style={{ display: 'block', textAlign: 'right', fontSize: '0.7rem', opacity: 0.55, marginTop: '0.25rem' }}>
                  {(formData[COURT_REMINDERS_TEXT_CONFIG_KEY] || '').length} / {COURT_REMINDERS_TEXT_MAX_LENGTH}
                </span>
              </div>

              {/* Same renderer as the guest popup, so what the admin sees here
                  is how the bullets and line breaks will look to a guest. */}
              {(formData[COURT_REMINDERS_TEXT_CONFIG_KEY] || '').trim() && (
                <div style={{ maxWidth: '720px' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: COLORS.ink, marginBottom: '0.4rem' }}>
                    Preview
                  </div>
                  <div
                    style={{
                      padding: '0.9rem 1rem', border: `1px solid ${COLORS.line}`, borderRadius: '10px',
                      background: COLORS.surface, color: COLORS.inkSoft, fontSize: '0.88rem', lineHeight: 1.6,
                    }}
                  >
                    <FormattedText text={formData[COURT_REMINDERS_TEXT_CONFIG_KEY]} />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Photos Tab */}
        {activeTab === 'photos' && (
          <div className="config-section">
            <h3 style={{ color: COLORS.ink, marginBottom: '0.5rem', fontSize: '0.9rem' }}>Court Photo Gallery</h3>
            <p style={{ opacity: 0.7, fontSize: '0.8rem', marginBottom: '0.75rem' }}>
              Shown as a collage above the booking calendar (1 large photo + up to 3 smaller ones). Guests can click
              "Show all photos" to browse every photo here, even ones not shown in the collage. Leave empty to use
              the default demo photos.
            </p>

            <div
              style={{
                background: COLORS.cardBg,
                padding: '0.75rem',
                borderRadius: '6px',
                marginBottom: '1rem',
                border: `1px solid ${COLORS.line}`,
              }}
            >
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: COLORS.neonLime,
                  color: COLORS.deepBg,
                  border: 'none',
                  borderRadius: '4px',
                  cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '0.8rem',
                  opacity: uploadingPhoto ? 0.7 : 1,
                }}
              >
                {uploadingPhoto ? 'Uploading...' : '+ Add Photo'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAddGalleryPhoto}
                  disabled={uploadingPhoto}
                  style={{ display: 'none' }}
                />
              </label>
              <span style={{ marginLeft: '0.75rem', opacity: 0.6, fontSize: '0.75rem' }}>Max 800KB per photo</span>
            </div>

            <div className="qr-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.75rem' }}>
              {(formData.galleryPhotos || []).map((photo, idx) => (
                <div
                  key={photo.id}
                  style={{
                    background: COLORS.cardBg,
                    padding: '0.5rem',
                    borderRadius: '6px',
                    border: `1px solid ${COLORS.line}`,
                    textAlign: 'center',
                  }}
                >
                  <img
                    src={photo.url}
                    alt={`Court ${idx + 1}`}
                    style={{ width: '100%', height: '90px', objectFit: 'cover', borderRadius: '4px', marginBottom: '0.5rem' }}
                  />
                  <button
                    onClick={() => handleDeleteGalleryPhoto(photo.id)}
                    style={{
                      padding: '0.3rem 0.5rem',
                      background: 'rgba(192, 57, 43, 0.1)',
                      color: '#c0392b',
                      border: '1px solid rgba(192, 57, 43, 0.35)',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.7rem',
                    }}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
            {(!formData.galleryPhotos || formData.galleryPhotos.length === 0) && (
              <p style={{ opacity: 0.6, fontSize: '0.8rem' }}>No custom photos yet — the default demo photos are showing instead.</p>
            )}

            {(formData.sports || []).length > 1 && (
              <>
                <h3 style={{ color: COLORS.ink, margin: '2rem 0 0.5rem', fontSize: '0.9rem' }}>Per-Sport Photo Galleries</h3>
                <p style={{ opacity: 0.7, fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                  Optional — once a guest picks a sport, they see that sport's own photos here instead of the shared
                  gallery above. Leave a sport empty to keep showing it the shared gallery.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {(formData.sports || []).map(sport => (
                    <div
                      key={sport.id}
                      style={{
                        background: COLORS.cardBg, padding: '0.85rem', borderRadius: '8px',
                        border: `1px solid ${COLORS.line}`,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem' }}>
                        <span style={{ fontWeight: 700, fontSize: '0.85rem', color: COLORS.ink }}>
                          {sport.icon ? `${sport.icon} ` : ''}{sport.name}
                        </span>
                        <label
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.35rem 0.7rem',
                            background: COLORS.neonLime, color: COLORS.deepBg, borderRadius: '4px',
                            cursor: uploadingSportGalleryId === sport.id ? 'not-allowed' : 'pointer',
                            fontWeight: '600', fontSize: '0.74rem',
                            opacity: uploadingSportGalleryId === sport.id ? 0.7 : 1,
                          }}
                        >
                          {uploadingSportGalleryId === sport.id ? 'Uploading...' : '+ Add Photo'}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleAddSportGalleryPhoto(sport.id, e)}
                            disabled={uploadingSportGalleryId === sport.id}
                            style={{ display: 'none' }}
                          />
                        </label>
                        <span style={{ opacity: 0.6, fontSize: '0.72rem' }}>Max 800KB per photo</span>
                      </div>
                      {(sport.galleryPhotos || []).length > 0 ? (
                        <div className="qr-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.6rem' }}>
                          {sport.galleryPhotos.map((photo, idx) => (
                            <div
                              key={photo.id}
                              style={{
                                background: '#fff', padding: '0.4rem', borderRadius: '6px',
                                border: `1px solid ${COLORS.line}`, textAlign: 'center',
                              }}
                            >
                              <img
                                src={photo.url}
                                alt={`${sport.name} ${idx + 1}`}
                                style={{ width: '100%', height: '75px', objectFit: 'cover', borderRadius: '4px', marginBottom: '0.4rem' }}
                              />
                              <button
                                onClick={() => handleDeleteSportGalleryPhoto(sport.id, photo.id)}
                                style={{
                                  padding: '0.25rem 0.45rem', background: 'rgba(192, 57, 43, 0.1)', color: '#c0392b',
                                  border: '1px solid rgba(192, 57, 43, 0.35)', borderRadius: '4px',
                                  cursor: 'pointer', fontSize: '0.68rem',
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{ opacity: 0.6, fontSize: '0.76rem', margin: 0 }}>No {sport.name.toLowerCase()}-specific photos yet — showing the shared gallery for this sport.</p>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Landing Page Tab */}
        {activeTab === 'landing' && (
          <div className="config-section">
            {/* Headline first — it's the top of the landing page. */}
            <h3 style={{ color: COLORS.ink, marginBottom: '0.5rem', fontSize: '0.9rem' }}>Headline Section</h3>
            <p style={{ opacity: 0.7, fontSize: '0.8rem', marginBottom: '0.75rem' }}>
              The big heading at the very top of the landing page. Leave any field blank to keep the
              auto-generated wording.
            </p>
            <div
              style={{
                background: COLORS.cardBg, padding: '0.75rem', borderRadius: '6px',
                marginBottom: '2rem', border: `1px solid ${COLORS.line}`,
                display: 'flex', flexDirection: 'column', gap: '0.75rem',
              }}
            >
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{ fontSize: '0.7rem' }}>Title</label>
                <input
                  type="text"
                  value={formData.heroTitle || ''}
                  onChange={(e) => handleChange('heroTitle', e.target.value)}
                  placeholder={`Play ${(formData.sports || []).map(s => s.name).join(' & ') || 'Pickleball'} at ${formData.courtName || 'your club'}`}
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{ fontSize: '0.7rem' }}>Description</label>
                <textarea
                  rows={2}
                  value={formData.heroDescription || ''}
                  onChange={(e) => handleChange('heroDescription', e.target.value)}
                  placeholder="e.g., 8 courts — book online in minutes."
                  style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{ fontSize: '0.7rem' }}>What we offer (tags)</label>
                <textarea
                  rows={2}
                  value={heroTagsText}
                  onChange={(e) => handleHeroTagsChange(e.target.value)}
                  placeholder="6 Pickleball courts, 2 Tennis courts, Open play, Beginner friendly"
                  style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
                />
                <p style={{ opacity: 0.65, fontSize: '0.72rem', margin: '0.35rem 0 0' }}>
                  Separate each one with a comma — they render as the small pills under the buttons. Leave blank
                  to auto-generate them from your court counts.
                </p>
              </div>
            </div>

            <h3 style={{ color: COLORS.ink, marginBottom: '0.5rem', fontSize: '0.9rem' }}>Club Logo</h3>
            <p style={{ opacity: 0.7, fontSize: '0.8rem', marginBottom: '0.75rem' }}>
              Shown in the landing page header and footer. Leave empty to use a placeholder icon.
            </p>
            <div
              style={{
                background: COLORS.cardBg,
                padding: '0.75rem',
                borderRadius: '6px',
                marginBottom: '2rem',
                border: `1px solid ${COLORS.line}`,
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              {formData.logoUrl ? (
                <img
                  src={formData.logoUrl}
                  alt="Club logo"
                  style={{ width: '56px', height: '56px', objectFit: 'contain', borderRadius: '50%', background: '#fff' }}
                />
              ) : (
                <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: COLORS.cardBg, border: `1px solid ${COLORS.line}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem' }}>
                  🏓
                </div>
              )}
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: COLORS.neonLime,
                  color: COLORS.deepBg,
                  border: 'none',
                  borderRadius: '4px',
                  cursor: uploadingLogo ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '0.8rem',
                  opacity: uploadingLogo ? 0.7 : 1,
                }}
              >
                {uploadingLogo ? 'Uploading...' : formData.logoUrl ? 'Replace Logo' : '+ Upload Logo'}
                <input type="file" accept="image/*" onChange={handleUploadLogo} disabled={uploadingLogo} style={{ display: 'none' }} />
              </label>
              {formData.logoUrl && (
                <button
                  onClick={handleRemoveLogo}
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: 'rgba(192, 57, 43, 0.1)',
                    color: '#c0392b',
                    border: '1px solid rgba(192, 57, 43, 0.35)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                  }}
                >
                  Remove
                </button>
              )}
              <span style={{ opacity: 0.6, fontSize: '0.75rem' }}>Max 300KB, square images work best</span>
            </div>

            <h3 style={{ color: COLORS.ink, marginBottom: '0.5rem', fontSize: '0.9rem' }}>Landing Page Photos</h3>
            <p style={{ opacity: 0.7, fontSize: '0.8rem', marginBottom: '0.75rem' }}>
              Shown in the hero collage at the top of the landing page. Falls back to the court gallery photos (Photos
              tab), then a placeholder, when empty.
            </p>

            <div
              style={{
                background: COLORS.cardBg,
                padding: '0.75rem',
                borderRadius: '6px',
                marginBottom: '1rem',
                border: `1px solid ${COLORS.line}`,
              }}
            >
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: COLORS.neonLime,
                  color: COLORS.deepBg,
                  border: 'none',
                  borderRadius: '4px',
                  cursor: uploadingLandingPhoto ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '0.8rem',
                  opacity: uploadingLandingPhoto ? 0.7 : 1,
                }}
              >
                {uploadingLandingPhoto ? 'Uploading...' : '+ Add Photo'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAddLandingImage}
                  disabled={uploadingLandingPhoto}
                  style={{ display: 'none' }}
                />
              </label>
              <span style={{ marginLeft: '0.75rem', opacity: 0.6, fontSize: '0.75rem' }}>Max 800KB per photo</span>
            </div>

            <div className="qr-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.75rem' }}>
              {(formData.landingImages || []).map((photo, idx) => (
                <div
                  key={photo.id}
                  style={{
                    background: COLORS.cardBg,
                    padding: '0.5rem',
                    borderRadius: '6px',
                    border: `1px solid ${COLORS.line}`,
                    textAlign: 'center',
                  }}
                >
                  <img
                    src={photo.url}
                    alt={`Landing ${idx + 1}`}
                    style={{ width: '100%', height: '90px', objectFit: 'cover', borderRadius: '4px', marginBottom: '0.5rem' }}
                  />
                  <button
                    onClick={() => handleDeleteLandingImage(photo.id)}
                    style={{
                      padding: '0.3rem 0.5rem',
                      background: 'rgba(192, 57, 43, 0.1)',
                      color: '#c0392b',
                      border: '1px solid rgba(192, 57, 43, 0.35)',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.7rem',
                    }}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
            {(!formData.landingImages || formData.landingImages.length === 0) && (
              <p style={{ opacity: 0.6, fontSize: '0.8rem' }}>No custom photos yet — falling back to the court gallery photos, or a placeholder if none exist.</p>
            )}

            {(formData.sports || []).length > 1 && (
              <>
                <h3 style={{ color: COLORS.ink, margin: '2rem 0 0.5rem', fontSize: '0.9rem' }}>Pick Your Court — Sport Cards</h3>
                <p style={{ opacity: 0.7, fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                  Shown in the "Pick your court" section, one card per sport (Sports & Courts tab adds/removes the
                  sports themselves). Each card needs its own photo and description.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {(formData.sports || []).map(sport => (
                    <div
                      key={sport.id}
                      style={{
                        display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start',
                        background: COLORS.cardBg, padding: '0.85rem', borderRadius: '8px',
                        border: `1px solid ${COLORS.line}`,
                      }}
                    >
                      <div style={{ flex: '0 0 auto' }}>
                        {sport.landingImageUrl ? (
                          <img
                            src={sport.landingImageUrl}
                            alt={sport.name}
                            style={{ width: '120px', height: '80px', objectFit: 'cover', borderRadius: '6px' }}
                          />
                        ) : (
                          <div style={{
                            width: '120px', height: '80px', borderRadius: '6px', background: '#fff',
                            border: `1px dashed ${COLORS.line}`, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', fontSize: '1.5rem',
                          }}>
                            {sport.icon || '🎾'}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.4rem' }}>
                          <label
                            style={{
                              display: 'inline-flex', alignItems: 'center', padding: '0.3rem 0.6rem',
                              background: COLORS.neonLime, color: COLORS.deepBg, borderRadius: '4px',
                              cursor: uploadingSportImageId === sport.id ? 'not-allowed' : 'pointer',
                              fontWeight: '600', fontSize: '0.72rem',
                              opacity: uploadingSportImageId === sport.id ? 0.7 : 1,
                            }}
                          >
                            {uploadingSportImageId === sport.id ? 'Uploading...' : sport.landingImageUrl ? 'Replace' : 'Upload'}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleUploadSportImage(sport.id, e)}
                              disabled={uploadingSportImageId === sport.id}
                              style={{ display: 'none' }}
                            />
                          </label>
                          {sport.landingImageUrl && (
                            <button
                              onClick={() => handleRemoveSportImage(sport.id)}
                              style={{
                                padding: '0.3rem 0.6rem', background: 'rgba(192, 57, 43, 0.1)', color: '#c0392b',
                                border: '1px solid rgba(192, 57, 43, 0.35)', borderRadius: '4px',
                                cursor: 'pointer', fontSize: '0.72rem',
                              }}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                      <div style={{ flex: '1 1 220px', minWidth: '220px' }}>
                        <label style={{ fontSize: '0.78rem', fontWeight: 600, color: COLORS.ink, display: 'block', marginBottom: '0.3rem' }}>
                          {sport.icon ? `${sport.icon} ` : ''}{sport.name} card description
                        </label>
                        <textarea
                          value={sport.landingDescription || ''}
                          onChange={(e) => updateSport(sport.id, 'landingDescription', e.target.value)}
                          placeholder={`e.g. Six standard courts — three indoor, three outdoor — finished with silica sand.`}
                          rows={3}
                          style={{
                            width: '100%', padding: '0.5rem 0.65rem', fontSize: '0.82rem', borderRadius: '4px',
                            border: `1px solid ${COLORS.line}`, resize: 'vertical', fontFamily: 'inherit',
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* About / story section — last on this tab, matching the
                order these sections appear down the landing page itself. */}
            <h3 style={{ color: COLORS.ink, margin: '2rem 0 0.5rem', fontSize: '0.9rem' }}>About This Club</h3>
            <p style={{ opacity: 0.7, fontSize: '0.8rem', marginBottom: '0.75rem' }}>
              The story section partway down the landing page. Leave any field blank to keep the
              auto-generated wording.
            </p>
            <div
              style={{
                background: COLORS.cardBg, padding: '0.75rem', borderRadius: '6px',
                border: `1px solid ${COLORS.line}`,
                display: 'flex', flexDirection: 'column', gap: '0.75rem',
              }}
            >
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{ fontSize: '0.7rem' }}>Eyebrow (small label above the heading)</label>
                <input
                  type="text"
                  value={formData.aboutEyebrow || ''}
                  onChange={(e) => handleChange('aboutEyebrow', e.target.value)}
                  placeholder={`Welcome to ${formData.courtName || 'your club'}`}
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{ fontSize: '0.7rem' }}>Title</label>
                <input
                  type="text"
                  value={formData.aboutTitle || ''}
                  onChange={(e) => handleChange('aboutTitle', e.target.value)}
                  placeholder="e.g., A home for pickleball & tennis near Legazpi"
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{ fontSize: '0.7rem' }}>Story / description</label>
                <textarea
                  rows={6}
                  value={formData.aboutStory || ''}
                  onChange={(e) => handleChange('aboutStory', e.target.value)}
                  placeholder={'How the club started, what makes it different, who it\'s for…\n\nLeave a blank line between paragraphs to split them.'}
                  style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
                />
                <p style={{ opacity: 0.65, fontSize: '0.72rem', margin: '0.35rem 0 0' }}>
                  Separate paragraphs with a blank line. Your Court Details cards still appear below this text.
                </p>
              </div>

              {/* Photos stacked beside the story, matching the landing
                  page's two-column About layout. */}
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{ fontSize: '0.7rem' }}>Photos</label>
                <p style={{ opacity: 0.65, fontSize: '0.72rem', margin: '0 0 0.5rem' }}>
                  Shown stacked beside the story. Two works best. Max 800KB each — falls back to your hero
                  photos when empty.
                </p>
                <label
                  style={{
                    display: 'inline-block', background: COLORS.cyanDeep, color: '#fff',
                    padding: '0.45rem 0.9rem', borderRadius: '8px', fontSize: '0.8rem',
                    cursor: uploadingAboutPhoto ? 'not-allowed' : 'pointer',
                    opacity: uploadingAboutPhoto ? 0.7 : 1, marginBottom: '0.6rem',
                  }}
                >
                  {uploadingAboutPhoto ? 'Uploading...' : '+ Add Photo'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAddAboutImage}
                    disabled={uploadingAboutPhoto}
                    style={{ display: 'none' }}
                  />
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
                  {(formData.aboutImages || []).map(photo => (
                    <div key={photo.id} style={{ position: 'relative' }}>
                      <img
                        src={photo.url}
                        alt="About section"
                        style={{
                          width: '110px', height: '80px', objectFit: 'cover',
                          borderRadius: '8px', border: `1px solid ${COLORS.line}`, display: 'block',
                        }}
                      />
                      <button
                        onClick={() => handleDeleteAboutImage(photo.id)}
                        aria-label="Remove photo"
                        style={{
                          position: 'absolute', top: '-6px', right: '-6px', width: '22px', height: '22px',
                          borderRadius: '50%', border: 'none', background: '#c0392b', color: '#fff',
                          cursor: 'pointer', fontSize: '0.75rem', lineHeight: 1,
                        }}
                      >✕</button>
                    </div>
                  ))}
                  {(formData.aboutImages || []).length === 0 && (
                    <p style={{ opacity: 0.6, fontSize: '0.78rem', margin: 0 }}>No photos yet.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Services — the "What we offer" section further down the page. */}
            <h3 style={{ color: COLORS.ink, margin: '2rem 0 0.5rem', fontSize: '0.9rem' }}>Services</h3>
            <p style={{ opacity: 0.7, fontSize: '0.8rem', marginBottom: '0.75rem' }}>
              The &quot;What we offer&quot; section: quick tags, then cards describing what makes the club worth booking.
            </p>

            <div
              style={{
                background: COLORS.cardBg, padding: '0.75rem', borderRadius: '6px',
                border: `1px solid ${COLORS.line}`, marginBottom: '0.75rem',
              }}
            >
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{ fontSize: '0.7rem' }}>Service tags</label>
                <textarea
                  rows={3}
                  value={serviceTagsText}
                  onChange={(e) => handleServiceTagsChange(e.target.value)}
                  placeholder="Court booking, Open play, Coaching & lessons, Tournaments"
                  style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
                />
                <p style={{ opacity: 0.65, fontSize: '0.72rem', margin: '0.35rem 0 0' }}>
                  Separate each one with a comma. They render as the small pill buttons.
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '0.75rem' }}>
              {(formData.serviceCards || []).map((card, index) => (
                <div
                  key={card.id}
                  style={{
                    background: COLORS.cardBg, padding: '0.75rem', borderRadius: '10px',
                    border: `1px solid ${COLORS.line}`, display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.7rem' }}>Title</label>
                      <input
                        type="text"
                        value={card.title || ''}
                        onChange={(e) => updateServiceCard(card.id, 'title', e.target.value)}
                        placeholder="e.g., Easy online booking"
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.7rem' }}>Description</label>
                      <textarea
                        rows={2}
                        value={card.description || ''}
                        onChange={(e) => updateServiceCard(card.id, 'description', e.target.value)}
                        placeholder="e.g., Pick your date, court and hours online, then pay and upload your receipt."
                        style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: '0 0 auto' }}>
                    <button
                      onClick={() => moveServiceCard(card.id, -1)}
                      disabled={index === 0}
                      aria-label="Move up"
                      title="Move up"
                      style={{
                        background: 'none', border: 'none', cursor: index === 0 ? 'not-allowed' : 'pointer',
                        opacity: index === 0 ? 0.3 : 0.7, fontSize: '0.9rem', padding: '0.1rem 0.3rem',
                      }}
                    >▲</button>
                    <button
                      onClick={() => moveServiceCard(card.id, 1)}
                      disabled={index === (formData.serviceCards || []).length - 1}
                      aria-label="Move down"
                      title="Move down"
                      style={{
                        background: 'none', border: 'none',
                        cursor: index === (formData.serviceCards || []).length - 1 ? 'not-allowed' : 'pointer',
                        opacity: index === (formData.serviceCards || []).length - 1 ? 0.3 : 0.7,
                        fontSize: '0.9rem', padding: '0.1rem 0.3rem',
                      }}
                    >▼</button>
                    <button
                      onClick={() => handleRemoveServiceCard(card.id)}
                      aria-label="Remove card"
                      title="Remove"
                      style={{
                        background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer',
                        fontSize: '0.85rem', padding: '0.1rem 0.3rem',
                      }}
                    >✕</button>
                  </div>
                </div>
              ))}
              {(formData.serviceCards || []).length === 0 && (
                <p style={{ opacity: 0.6, fontSize: '0.8rem' }}>No service cards yet.</p>
              )}
            </div>

            <button
              onClick={handleAddServiceCard}
              style={{
                background: 'none', border: `1px dashed ${COLORS.line}`, color: COLORS.ink,
                padding: '0.5rem 0.9rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem',
              }}
            >
              + Add service card
            </button>

            {/* FAQs — last section on the landing page. */}
            <h3 style={{ color: COLORS.ink, margin: '2rem 0 0.5rem', fontSize: '0.9rem' }}>FAQs</h3>
            <p style={{ opacity: 0.7, fontSize: '0.8rem', marginBottom: '0.75rem' }}>
              The accordion at the bottom of the landing page. Leave this empty to keep the auto-generated
              questions, whose answers update themselves from your rates, court counts and policies. Adding
              any question here replaces that whole list.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '0.75rem' }}>
              {(formData.faqs || []).map((faq, index) => (
                <div
                  key={faq.id}
                  style={{
                    background: COLORS.cardBg, padding: '0.75rem', borderRadius: '10px',
                    border: `1px solid ${COLORS.line}`, display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.7rem' }}>Question</label>
                      <input
                        type="text"
                        value={faq.question || ''}
                        onChange={(e) => updateFaq(faq.id, 'question', e.target.value)}
                        placeholder="e.g., Is parking available?"
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.7rem' }}>Answer</label>
                      <textarea
                        rows={3}
                        value={faq.answer || ''}
                        onChange={(e) => updateFaq(faq.id, 'answer', e.target.value)}
                        placeholder="e.g., Yes — there's room for cars and motorcycles beside the courts."
                        style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: '0 0 auto' }}>
                    <button
                      onClick={() => moveFaq(faq.id, -1)}
                      disabled={index === 0}
                      aria-label="Move up"
                      title="Move up"
                      style={{
                        background: 'none', border: 'none', cursor: index === 0 ? 'not-allowed' : 'pointer',
                        opacity: index === 0 ? 0.3 : 0.7, fontSize: '0.9rem', padding: '0.1rem 0.3rem',
                      }}
                    >▲</button>
                    <button
                      onClick={() => moveFaq(faq.id, 1)}
                      disabled={index === (formData.faqs || []).length - 1}
                      aria-label="Move down"
                      title="Move down"
                      style={{
                        background: 'none', border: 'none',
                        cursor: index === (formData.faqs || []).length - 1 ? 'not-allowed' : 'pointer',
                        opacity: index === (formData.faqs || []).length - 1 ? 0.3 : 0.7,
                        fontSize: '0.9rem', padding: '0.1rem 0.3rem',
                      }}
                    >▼</button>
                    <button
                      onClick={() => handleRemoveFaq(faq.id)}
                      aria-label="Remove question"
                      title="Remove"
                      style={{
                        background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer',
                        fontSize: '0.85rem', padding: '0.1rem 0.3rem',
                      }}
                    >✕</button>
                  </div>
                </div>
              ))}
              {(formData.faqs || []).length === 0 && (
                <p style={{ opacity: 0.6, fontSize: '0.8rem' }}>
                  Using the auto-generated questions. Add your own below to take over.
                </p>
              )}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                onClick={handleAddFaq}
                style={{
                  background: 'none', border: `1px dashed ${COLORS.line}`, color: COLORS.ink,
                  padding: '0.5rem 0.9rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem',
                }}
              >
                + Add question
              </button>
              {(formData.faqs || []).length === 0 && (
                <button
                  onClick={handleLoadDefaultFaqs}
                  title="Copies the current auto-generated questions here so you can edit them"
                  style={{
                    background: 'none', border: `1px dashed ${COLORS.line}`, color: COLORS.ink,
                    padding: '0.5rem 0.9rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem',
                  }}
                >
                  Start from the default questions
                </button>
              )}
            </div>
          </div>
        )}

        {/* QR Codes Tab */}
        {activeTab === 'payment' && (
          <div className="config-section">
            <h3 style={{ color: COLORS.ink, marginBottom: '0.75rem', fontSize: '0.9rem' }}>Payment QR Codes</h3>

            {/* Add New QR */}
            <div
              style={{
                background: COLORS.cardBg,
                padding: '0.75rem',
                borderRadius: '6px',
                marginBottom: '1rem',
                border: `1px solid ${COLORS.line}`,
              }}
            >
              <h4 style={{ color: COLORS.ink, marginTop: 0, marginBottom: '0.75rem', fontSize: '0.85rem' }}>Add New QR Code</h4>
              <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.5rem' }}>
                <div className="form-group">
                  <label>Bank name</label>
                  <input
                    type="text"
                    value={qrName}
                    onChange={(e) => setQrName(e.target.value)}
                    placeholder="e.g., GCash"
                  />
                </div>
                <div className="form-group">
                  <label>Upload Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleQRFileChange}
                    style={{ fontSize: '0.75rem' }}
                  />
                </div>
                <div className="form-group">
                  <label>Account name</label>
                  <input
                    type="text"
                    value={accountName}
                    onChange={(e) => setAccountName(e.target.value)}
                    placeholder="(Optional)"
                  />
                </div>
                <div className="form-group">
                  <label>Account #</label>
                  <input
                    type="text"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    placeholder="(Optional)"
                  />
                </div>
              </div>
              <button
                onClick={handleAddQRCode}
                disabled={uploadingQR}
                style={{
                  padding: '0.5rem 1rem',
                  background: COLORS.neonLime,
                  color: COLORS.deepBg,
                  border: 'none',
                  borderRadius: '4px',
                  cursor: uploadingQR ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '0.8rem',
                  opacity: uploadingQR ? 0.7 : 1,
                  marginTop: '0.5rem',
                }}
              >
                {uploadingQR ? 'Uploading...' : 'Add QR Code'}
              </button>
            </div>

            {/* Existing QR Codes */}
            <div>
              <h4 style={{ color: COLORS.ink, marginBottom: '0.75rem', fontSize: '0.85rem' }}>Existing QR Codes</h4>
              <div className="qr-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.75rem' }}>
                {(formData.qrCodes || []).map((qr) => (
                  <div
                    key={qr.id}
                    style={{
                      background: COLORS.cardBg,
                      padding: '0.5rem',
                      borderRadius: '6px',
                      border: `1px solid ${COLORS.line}`,
                      textAlign: 'center',
                    }}
                  >
                    <img
                      src={qr.url}
                      alt={qr.name}
                      style={{
                        width: '100%',
                        maxHeight: '80px',
                        objectFit: 'contain',
                        borderRadius: '4px',
                        marginBottom: '0.5rem',
                      }}
                    />
                    <p style={{ color: COLORS.ink, fontWeight: '600', margin: '0 0 0.5rem 0', fontSize: '0.8rem' }}>{qr.name}</p>
                    <button
                      onClick={() => handleDeleteQRCode(qr.id, qr.url)}
                      style={{
                        padding: '0.3rem 0.5rem',
                        background: 'rgba(192, 57, 43, 0.1)',
                        color: '#c0392b',
                        border: '1px solid rgba(192, 57, 43, 0.35)',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.7rem',
                      }}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
              {(!formData.qrCodes || formData.qrCodes.length === 0) && (
                <p style={{ opacity: 0.6, fontSize: '0.8rem' }}>No QR codes configured. Add one above.</p>
              )}
            </div>

            {/* ---- Court Owners ----
                Only rendered while Shared Ownership is on (Config -> System).
                A co-owned club pays each owner into their own account, so
                these QR codes — not the club-wide list above — are what a
                guest sees at checkout, chosen by which court they booked. */}
            {formData.sharedOwnershipEnabled === true && (
              <div style={{ marginTop: '1.5rem', borderTop: `1px solid ${COLORS.line}`, paddingTop: '1rem' }}>
                <h3 style={{ color: COLORS.ink, marginBottom: '0.35rem', fontSize: '0.9rem' }}>Court Owners</h3>
                <p style={{ opacity: 0.65, fontSize: '0.78rem', margin: '0 0 0.75rem' }}>
                  Add one entry per owner, then assign courts to them under Sports &amp; Courts. Each owner needs at
                  least one QR code — checkout shows only the QR of the owner whose court the guest booked, and a
                  court whose owner has no QR cannot be booked online. Owners are saved when you press
                  &quot;Save Configuration&quot; below.
                </p>

                <OwnershipWarningBanner config={formData} context="config" />

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '0.5rem' }}>
                  {(formData.owners || []).map(owner => {
                    const ownerCourts = (formData.courts || [])
                      .filter(c => String(c.ownerId) === String(owner.id))
                      .sort((a, b) => Number(a.number) - Number(b.number));
                    return (
                      <div
                        key={owner.id}
                        style={{
                          display: 'flex', flexDirection: 'column', gap: '0.55rem',
                          background: COLORS.cardBg, padding: '0.6rem 0.75rem', borderRadius: '8px',
                          border: `1px solid ${COLORS.line}`,
                        }}
                      >
                        {/* Row 1 — who the owner is. */}
                        <div style={COURT_CARD_ROW}>
                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '0.7rem' }}>Owner name</label>
                            <input
                              type="text"
                              value={owner.name || ''}
                              onChange={(e) => updateOwner(owner.id, 'name', e.target.value)}
                              placeholder="e.g. Juan Dela Cruz"
                              style={{ minWidth: '160px' }}
                            />
                          </div>
                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '0.7rem' }}>Contact number</label>
                            <input
                              type="tel"
                              value={owner.contactNumber || ''}
                              onChange={(e) => updateOwner(owner.id, 'contactNumber', e.target.value)}
                              placeholder="e.g. 0917 123 4567"
                              style={{ minWidth: '150px' }}
                            />
                          </div>
                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '0.7rem' }}>Email</label>
                            <input
                              type="email"
                              value={owner.email || ''}
                              onChange={(e) => updateOwner(owner.id, 'email', e.target.value)}
                              placeholder="(Optional)"
                              style={{ minWidth: '180px' }}
                            />
                          </div>
                          <button
                            onClick={() => handleRemoveOwner(owner.id)}
                            style={{
                              marginLeft: 'auto', alignSelf: 'center', background: 'none', border: 'none',
                              color: '#c0392b', cursor: 'pointer', fontSize: '0.85rem',
                            }}
                            aria-label="Remove owner"
                          >
                            ✕
                          </button>
                        </div>

                        {/* Which courts pay into this owner's account. Read-only
                            here — the assignment itself lives on the court card
                            (Sports & Courts) so there's exactly one place it can
                            be set, and this just reflects it back. */}
                        <div style={{ fontSize: '0.73rem', opacity: 0.75, borderTop: `1px dashed ${COLORS.line}`, paddingTop: '0.45rem' }}>
                          {ownerCourts.length > 0
                            ? <>Courts: {ownerCourts.map(c => (c.name || '').trim() || `Court ${c.number}`).join(', ')}</>
                            : <>No courts assigned yet — assign this owner on a court card under Sports &amp; Courts.</>}
                        </div>

                        {/* Row 2 — how this owner gets paid. */}
                        <div style={{ borderTop: `1px dashed ${COLORS.line}`, paddingTop: '0.5rem' }}>
                          <label style={{ fontSize: '0.7rem', display: 'block', marginBottom: '0.35rem' }}>
                            QR codes
                          </label>
                          <div className="qr-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.75rem' }}>
                            {(owner.qrCodes || []).map(qr => (
                              <div
                                key={qr.id}
                                style={{
                                  background: COLORS.surface, padding: '0.5rem', borderRadius: '6px',
                                  border: `1px solid ${COLORS.line}`, textAlign: 'center',
                                }}
                              >
                                <img
                                  src={qr.url}
                                  alt={qr.name || 'Payment QR'}
                                  style={{ width: '100%', maxHeight: '90px', objectFit: 'contain', borderRadius: '4px', marginBottom: '0.4rem' }}
                                />
                                <input
                                  type="text"
                                  value={qr.name || ''}
                                  onChange={(e) => updateOwnerQr(owner.id, qr.id, 'name', e.target.value)}
                                  placeholder="Bank name (e.g. GCash)"
                                  style={{ width: '100%', marginBottom: '0.3rem', fontSize: '0.72rem' }}
                                  aria-label="Bank name"
                                />
                                <input
                                  type="text"
                                  value={qr.accName || ''}
                                  onChange={(e) => updateOwnerQr(owner.id, qr.id, 'accName', e.target.value)}
                                  placeholder="Account name (optional)"
                                  style={{ width: '100%', marginBottom: '0.3rem', fontSize: '0.72rem' }}
                                  aria-label="Account name"
                                />
                                <input
                                  type="text"
                                  value={qr.accNumber || ''}
                                  onChange={(e) => updateOwnerQr(owner.id, qr.id, 'accNumber', e.target.value)}
                                  placeholder="Account # (optional)"
                                  style={{ width: '100%', marginBottom: '0.4rem', fontSize: '0.72rem' }}
                                  aria-label="Account number"
                                />
                                <button
                                  onClick={() => handleRemoveOwnerQr(owner.id, qr.id)}
                                  style={{
                                    padding: '0.25rem 0.5rem', background: 'rgba(192, 57, 43, 0.1)', color: '#c0392b',
                                    border: '1px solid rgba(192, 57, 43, 0.35)', borderRadius: '4px',
                                    cursor: 'pointer', fontSize: '0.68rem',
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            ))}
                          </div>
                          {(owner.qrCodes || []).length === 0 && (
                            <p style={{ margin: '0 0 0.4rem', fontSize: '0.74rem', color: '#c0392b', fontWeight: 600 }}>
                              No QR code yet — guests cannot check out on this owner's courts.
                            </p>
                          )}
                          <label
                            style={{
                              display: 'inline-block', marginTop: '0.45rem', padding: '0.35rem 0.7rem',
                              border: `1px dashed ${COLORS.line}`, borderRadius: '6px',
                              cursor: uploadingOwnerQrId === owner.id ? 'not-allowed' : 'pointer',
                              fontSize: '0.74rem', color: COLORS.textSecondary,
                              opacity: uploadingOwnerQrId === owner.id ? 0.6 : 1,
                            }}
                          >
                            {uploadingOwnerQrId === owner.id ? 'Uploading…' : '+ Upload QR code'}
                            <input
                              type="file"
                              accept="image/*"
                              disabled={uploadingOwnerQrId === owner.id}
                              onChange={(e) => handleOwnerQRFileChange(owner.id, e)}
                              style={{ display: 'none' }}
                            />
                          </label>
                        </div>
                      </div>
                    );
                  })}
                  {(formData.owners || []).length === 0 && (
                    <p style={{ opacity: 0.6, fontSize: '0.8rem' }}>No owners yet. Add one below.</p>
                  )}
                </div>
                <button
                  onClick={handleAddOwner}
                  style={{
                    padding: '0.45rem 0.8rem', background: 'none', border: `1px dashed ${COLORS.line}`,
                    borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem', color: COLORS.textSecondary,
                  }}
                >
                  + Add owner
                </button>
              </div>
            )}
          </div>
        )}

        {/* Theme Tab */}
        {activeTab === 'theme' && (
          <div className="config-section">
            <UiDesignSettings
              value={formData[UI_DESIGN_CONFIG_KEY]}
              onChange={(next) => handleChange(UI_DESIGN_CONFIG_KEY, next)}
            />

            <BrowserPromptSettings
              value={formData[BROWSER_PROMPT_CONFIG_KEY]}
              onChange={(next) => handleChange(BROWSER_PROMPT_CONFIG_KEY, next)}
            />

            <h3 style={{ color: COLORS.ink, marginBottom: '0.5rem', fontSize: '0.9rem' }}>Site Colors</h3>
            <p style={{ opacity: 0.7, fontSize: '0.8rem', marginBottom: '1rem' }}>
              Controls the color palette used across the booking site and admin dashboard. Accepts hex values (e.g. #6fb5ab).
            </p>
            <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
              {THEME_COLOR_FIELDS.map(field => (
                <ColorField
                  key={field.key}
                  label={field.label}
                  hint={field.hint}
                  value={formData[field.key] || DEFAULT_THEME_COLORS[field.key]}
                  onChange={(value) => handleChange(field.key, value)}
                />
              ))}
            </div>

            <SlotButtonColorSettings
              value={formData[SLOT_BUTTON_COLORS_CONFIG_KEY]}
              activeColor={formData.themeActiveColor || DEFAULT_THEME_COLORS.themeActiveColor}
              onChange={(next) => handleChange(SLOT_BUTTON_COLORS_CONFIG_KEY, next)}
            />
          </div>
        )}

        {/* Booking Settings Tab */}
        {activeTab === 'booking' && (
          <BookingSettingsTab
            value={formData[BOOKING_SETTINGS_CONFIG_KEY]}
            onChange={(next) => handleChange(BOOKING_SETTINGS_CONFIG_KEY, next)}
            courtOptions={slotSetCourtOptions}
          />
        )}

        {/* System Tab */}
        {activeTab === 'system' && (
          <div className="config-section">
            <h3 style={{ color: COLORS.ink, marginBottom: '0.5rem', fontSize: '0.9rem' }}>System</h3>
            <p style={{ opacity: 0.7, fontSize: '0.8rem', marginBottom: '1rem' }}>
              General site behavior settings.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <label style={SYSTEM_TOGGLE_CARD}>
                <input
                  type="checkbox"
                  checked={formData.showLandingPage ?? true}
                  onChange={(e) => handleChange('showLandingPage', e.target.checked)}
                  style={SYSTEM_TOGGLE_BOX}
                />
                <span>
                  <span style={{ display: 'block', fontWeight: 600, color: COLORS.ink, fontSize: '0.88rem' }}>
                    Show landing page
                  </span>
                  <span style={{ display: 'block', fontSize: '0.78rem', opacity: 0.7, marginTop: '2px' }}>
                    When unchecked, guests skip the marketing landing page entirely and go straight to the court/time
                    booking screen.
                  </span>
                </span>
              </label>

              {/* Whether an idle admin is signed out automatically. Off suits
                  a club running the dashboard on its own front-desk machine;
                  on suits a shared or public computer. Either way the admin
                  stays signed in across a refresh — this only controls the
                  idle window (see authServiceUtil.js). */}
              <label style={SYSTEM_TOGGLE_CARD}>
                <input
                  type="checkbox"
                  checked={formData.adminIdleTimeoutEnabled !== false}
                  onChange={(e) => handleChange('adminIdleTimeoutEnabled', e.target.checked)}
                  style={SYSTEM_TOGGLE_BOX}
                />
                <span>
                  <span style={{ display: 'block', fontWeight: 600, color: COLORS.ink, fontSize: '0.88rem' }}>
                    Auto log out idle admins
                  </span>
                  <span style={{ display: 'block', fontSize: '0.78rem', opacity: 0.7, marginTop: '2px' }}>
                    Signs an admin out after {ADMIN_IDLE_TIMEOUT_MINUTES} minutes with no activity. Uncheck to keep
                    admins signed in on their device until they log out themselves — convenient on a private
                    front-desk computer, riskier on a shared or public one.
                  </span>
                </span>
              </label>

              {/* Past dates on the Manage availability grid. Off by default
                  (read as `=== true`), keeping admins on today and later
                  until the club opts in. */}
              <label style={SYSTEM_TOGGLE_CARD}>
                <input
                  type="checkbox"
                  checked={formData.adminViewPastDatesEnabled === true}
                  onChange={(e) => handleChange('adminViewPastDatesEnabled', e.target.checked)}
                  style={SYSTEM_TOGGLE_BOX}
                />
                <span>
                  <span style={{ display: 'block', fontWeight: 600, color: COLORS.ink, fontSize: '0.88rem' }}>
                    View past dates
                  </span>
                  <span style={{ display: 'block', fontSize: '0.78rem', opacity: 0.7, marginTop: '2px' }}>
                    Lets admins open dates that have already passed on Manage availability, to record bookings
                    that were never entered, and confirm, decline or cancel finished reservations in Booking
                    Management. Uncheck to limit the grid to today and future dates and make finished
                    reservations read-only.
                  </span>
                </span>
              </label>

              {/* Which statuses the Manage availability dropdown offers.
                  A cell already showing an unticked status still displays
                  it — see cellOptions in AdminTimeSlotSelection.jsx. */}
              <div style={SYSTEM_FIELD_CARD}>
                <span>
                  <span style={{ display: 'block', fontWeight: 600, color: COLORS.ink, fontSize: '0.88rem' }}>
                    Availability status options
                  </span>
                  <span style={{ display: 'block', fontSize: '0.78rem', opacity: 0.7, marginTop: '2px' }}>
                    Choose which statuses appear in each time slot&rsquo;s dropdown on Manage availability. A slot
                    that already has an unticked status keeps showing it.
                  </span>
                  <span style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 1rem', marginTop: '0.5rem' }}>
                    {ADMIN_GRID_STATUS_OPTIONS.map(opt => (
                      <label
                        key={opt.value}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', fontWeight: 600, color: COLORS.ink, cursor: 'pointer' }}
                      >
                        <input
                          type="checkbox"
                          checked={getEnabledGridStatuses(formData).has(opt.value)}
                          onChange={(e) => handleGridStatusToggle(opt.value, e.target.checked)}
                          style={{ margin: 0, cursor: 'pointer' }}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </span>
                  {/* Only meaningful while Closed can be picked from the
                      dropdown, so it is hidden when Closed is unticked. The
                      saved value is kept either way. */}
                  {getEnabledGridStatuses(formData).has(SLOT_STATUS_CLOSED) && (
                    <label
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer',
                        marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: `1px solid ${COLORS.line}`,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isCloseReasonPromptEnabled(formData)}
                        onChange={(e) => handleChange(CLOSE_REASON_PROMPT_CONFIG_KEY, e.target.checked)}
                        style={{ margin: '2px 0 0', cursor: 'pointer', flexShrink: 0 }}
                      />
                      <span>
                        <span style={{ display: 'block', fontWeight: 600, color: COLORS.ink, fontSize: '0.82rem' }}>
                          Ask reason for closing timeslot
                        </span>
                        <span style={{ display: 'block', fontSize: '0.78rem', opacity: 0.7, marginTop: '2px' }}>
                          Shows a prompt for the reason whenever a slot is set to Closed. Uncheck to close slots
                          straight away with no reason recorded.
                        </span>
                      </span>
                    </label>
                  )}
                </span>
              </div>

              {/* The counter app at /pos. Unticking does not remove the
                  route — it serves a Coming Soon page there instead, so a
                  club still setting up its inventory can keep the link
                  shared without staff walking into a half-built till. Nothing
                  already recorded is touched: turning it back on returns the
                  same stock, sales and audit trail. */}
              <label style={SYSTEM_TOGGLE_CARD}>
                <input
                  type="checkbox"
                  checked={formData.posEnabled === true}
                  onChange={(e) => handleChange('posEnabled', e.target.checked)}
                  style={SYSTEM_TOGGLE_BOX}
                />
                <span>
                  <span style={{ display: 'block', fontWeight: 600, color: COLORS.ink, fontSize: '0.88rem' }}>
                    Point of Sale
                  </span>
                  <span style={{ display: 'block', fontSize: '0.78rem', opacity: 0.7, marginTop: '2px' }}>
                    Opens the counter app at /pos, where staff sign in to sell stock and see the day&rsquo;s
                    takings. Uncheck to show a Coming Soon page there instead — the screen is hidden, not
                    reset, so your inventory, sales and audit trail are all still there when you turn it
                    back on.
                  </span>
                </span>
              </label>

              {/* Co-owned clubs: each court belongs to a different owner who
                  collects payment into their own account. Off is the
                  original single-account product and changes nothing —
                  every helper this unlocks short-circuits on it (see
                  isSharedOwnershipEnabled in bookingUtils.js). */}
              <label style={SYSTEM_TOGGLE_CARD}>
                <input
                  type="checkbox"
                  checked={formData.sharedOwnershipEnabled === true}
                  onChange={(e) => handleChange('sharedOwnershipEnabled', e.target.checked)}
                  style={SYSTEM_TOGGLE_BOX}
                />
                <span>
                  <span style={{ display: 'block', fontWeight: 600, color: COLORS.ink, fontSize: '0.88rem' }}>
                    Shared Ownership
                  </span>
                  <span style={{ display: 'block', fontSize: '0.78rem', opacity: 0.7, marginTop: '2px' }}>
                    Allows the super admin to set an owner per court. Owners are added under Payment, each with
                    their own QR codes, and assigned to courts under Sports &amp; Courts. Guests can then only book
                    courts belonging to one owner at a time, and checkout shows just that owner's QR.
                  </span>
                </span>
              </label>

              {/* Open Play — club-wide rotation sessions guests join per
                  player, rather than reserving a court. Off by default and
                  read as `=== true` everywhere, so a club upgrading into
                  this build sees no change until it opts in. */}
              <label style={SYSTEM_TOGGLE_CARD}>
                <input
                  type="checkbox"
                  checked={formData.openPlayEnabled === true}
                  onChange={(e) => handleChange('openPlayEnabled', e.target.checked)}
                  style={SYSTEM_TOGGLE_BOX}
                />
                <span>
                  <span style={{ display: 'block', fontWeight: 600, color: COLORS.ink, fontSize: '0.88rem' }}>
                    Open Play
                  </span>
                  <span style={{ display: 'block', fontSize: '0.78rem', opacity: 0.7, marginTop: '2px' }}>
                    Lets you host Open Play sessions — club-wide rotations guests join per player instead of
                    booking a court. Adds the &ldquo;Host an Open Play session&rdquo; button to Manage availability.
                    Turning this off hides that button; sessions you have already hosted keep their slots on the
                    grid until you delete them.
                  </span>
                </span>
              </label>

              {/* Booking confirmation emails. Off by default — a club with
                  no email backend wired up should stay silent rather than fire
                  requests at an endpoint that isn't there. */}
              <label style={SYSTEM_TOGGLE_CARD}>
                <input
                  type="checkbox"
                  checked={formData.emailSendingEnabled === true}
                  onChange={(e) => handleChange('emailSendingEnabled', e.target.checked)}
                  style={SYSTEM_TOGGLE_BOX}
                />
                <span>
                  <span style={{ display: 'block', fontWeight: 600, color: COLORS.ink, fontSize: '0.88rem' }}>
                    Send booking emails
                  </span>
                  <span style={{ display: 'block', fontSize: '0.78rem', opacity: 0.7, marginTop: '2px' }}>
                    Emails guests their confirmation and status updates, and copies your notification address.
                    Leave this off until your email backend is set up &mdash; with nowhere to send to, every
                    booking would attempt a delivery that quietly fails. Bookings themselves work either way.
                  </span>
                </span>
              </label>

              {/* Display-only currency. Changing these does NOT convert
                  anything: no rate is rewritten and no booking is touched, so
                  a club switching the symbol is telling the app what its
                  existing numbers already meant. Rates and the processing fee
                  accept decimals, which is what makes a currency with
                  sub-unit pricing usable. */}
              <div className="system-field" style={SYSTEM_FIELD_CARD}>
                <span style={{ display: 'block', fontWeight: 600, color: COLORS.ink, fontSize: '0.88rem' }}>
                  Currency
                </span>
                <span style={{ display: 'block', fontSize: '0.78rem', opacity: 0.7, margin: '2px 0 0.6rem' }}>
                  The symbol shown in front of every amount across the site, receipts and exports. This changes
                  how prices are <em>displayed</em> only &mdash; nothing is converted, and your existing rates keep
                  the numbers they already have. Rates and the processing fee accept decimals
                  (e.g. {getCurrencySymbol(formData)}19.99).
                </span>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.8rem' }}>
                    <span style={{ opacity: 0.7 }}>Symbol</span>
                    <input
                      type="text"
                      maxLength={4}
                      value={formData.currencySymbol ?? DEFAULT_CURRENCY_SYMBOL}
                      onChange={(e) => handleChange('currencySymbol', e.target.value)}
                      placeholder={DEFAULT_CURRENCY_SYMBOL}
                      style={{ width: '90px' }}
                    />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.8rem', flex: '1 1 200px' }}>
                    <span style={{ opacity: 0.7 }}>Description</span>
                    <input
                      type="text"
                      maxLength={60}
                      value={formData.currencyDescription ?? DEFAULT_CURRENCY_DESCRIPTION}
                      onChange={(e) => handleChange('currencyDescription', e.target.value)}
                      placeholder={DEFAULT_CURRENCY_DESCRIPTION}
                    />
                  </label>
                </div>
                <span style={{ display: 'block', fontSize: '0.75rem', opacity: 0.6, marginTop: '0.5rem' }}>
                  Preview: {formatMoney(1234.5, formData)} &middot; {formatMoney(300, formData)}
                </span>
              </div>

              {/* How far ahead the public booking screen lets guests go.
                  Not a toggle, so it sits in a card of the same size rather
                  than loose under the switches. Deliberately guest-only:
                  the admin grid stays unlimited so staff can still take a
                  reservation further out over the phone. */}
              <div className="system-field" style={SYSTEM_FIELD_CARD}>
                <span style={{ display: 'block', fontWeight: 600, color: COLORS.ink, fontSize: '0.88rem' }}>
                  Advance booking window
                </span>
                <span style={{ display: 'block', fontSize: '0.78rem', opacity: 0.7, margin: '2px 0 0.6rem' }}>
                  How many days ahead guests may book. At {bookingWindowDays}, the booking screen offers today
                  through {bookingWindowPreview} and nothing later. Admins are not limited by this — you can still
                  book any date from Manage availability.
                </span>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem' }}>
                  <input
                    type="number"
                    min={MIN_BOOKING_WINDOW_DAYS}
                    max={MAX_BOOKING_WINDOW_DAYS}
                    value={formData.bookingWindowDays ?? DEFAULT_BOOKING_WINDOW_DAYS}
                    onChange={(e) => handleChange('bookingWindowDays', clampBookingWindowDays(e.target.value))}
                    style={{ width: '90px' }}
                  />
                  <span style={{ opacity: 0.7 }}>
                    days ahead ({MIN_BOOKING_WINDOW_DAYS}&ndash;{MAX_BOOKING_WINDOW_DAYS})
                  </span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${COLORS.line}` }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '0.6rem 1.5rem',
              background: COLORS.neonLime,
              color: COLORS.deepBg,
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              fontSize: '0.85rem',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>

      <Modal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
        confirmText={modalConfig.confirmText}
        onConfirm={modalConfig.onConfirm}
        showCancel={modalConfig.showCancel}
        cancelText={modalConfig.cancelText}
      />
    </div>
  );
}

export default AdminConfig;